/**
 * Clause Service
 * TanStack Query hooks for clause CRUD operations with Firestore
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';

// Query Keys
export const clauseKeys = {
  all: ['clauses'] as const,
  list: (filters?: ClauseFilters) => [...clauseKeys.all, 'list', filters] as const,
  detail: (id: string) => [...clauseKeys.all, 'detail', id] as const,
  byCategory: (category: string) => [...clauseKeys.all, 'category', category] as const,
};

// Types
export type ClauseCategory = 'Real Estate' | 'Business' | 'Estate Planning' | 'General' | 'NVCA' | 'ILPA' | 'YC SAFE' | 'Cooley GO' | 'Series Seed' | 'Litigation' | 'Family Law' | 'Employment' | 'Contracts';

export interface Clause {
  id: string;
  name: string;
  category: ClauseCategory;
  jurisdictions: string[];
  description: string;
  content: string;
  usageCount: number;
  lastModified: Date;
  createdAt: Date;
  createdBy: string;
  updatedBy: string;
}

export interface ClauseFormData {
  name: string;
  category: ClauseCategory;
  jurisdictions: string[];
  description?: string;
  content: string;
}

export interface ClauseFilters {
  category?: ClauseCategory;
  jurisdiction?: string;
  search?: string;
}

// Helper to convert Firestore timestamp to Date
function timestampToDate(timestamp: Timestamp | Date | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

// Generate unique ID
function generateId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `cls_${timestamp}${random}`;
}

// Mock data for development
const mockClauses: Clause[] = [
  {
    id: 'warranty_covenant',
    name: 'Warranty Covenant',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard warranty covenant for deed transfers',
    content: 'The Grantor hereby covenants with the Grantee that the Grantor is lawfully seized in fee simple of the above-described premises...',
    usageCount: 45,
    lastModified: new Date('2024-12-01'),
    createdAt: new Date('2024-01-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'quitclaim_covenant',
    name: 'Quitclaim Covenant',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Quitclaim covenant with no warranties',
    content: 'The Grantor conveys and quitclaims to the Grantee all interest which the Grantor may have in the above-described premises...',
    usageCount: 32,
    lastModified: new Date('2024-11-28'),
    createdAt: new Date('2024-01-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'homestead_waiver',
    name: 'Homestead Waiver',
    category: 'Real Estate',
    jurisdictions: ['FL'],
    description: 'Florida homestead rights waiver',
    content: 'The undersigned spouse hereby waives any and all homestead rights in and to the above-described property...',
    usageCount: 18,
    lastModified: new Date('2024-12-02'),
    createdAt: new Date('2024-02-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'marital_property_waiver',
    name: 'Marital Property Waiver',
    category: 'Real Estate',
    jurisdictions: ['WI'],
    description: 'Wisconsin Chapter 766 marital property waiver',
    content: 'The undersigned spouse hereby waives any interest in the above-described property pursuant to Wisconsin Statutes Chapter 766...',
    usageCount: 22,
    lastModified: new Date('2024-11-30'),
    createdAt: new Date('2024-02-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'notary_acknowledgment',
    name: 'Notary Acknowledgment',
    category: 'General',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard notary acknowledgment block',
    content: 'STATE OF {{state}}\nCOUNTY OF {{county}}\n\nThis instrument was acknowledged before me on {{date}}...',
    usageCount: 128,
    lastModified: new Date('2024-12-04'),
    createdAt: new Date('2024-01-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'self_proving_affidavit',
    name: 'Self-Proving Affidavit',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Affidavit for self-proving will',
    content: 'We, the undersigned witnesses, being duly sworn, declare to the undersigned authority that the testator...',
    usageCount: 15,
    lastModified: new Date('2024-12-03'),
    createdAt: new Date('2024-03-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'no_contest_clause',
    name: 'No Contest Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'In terrorem clause for wills and trusts',
    content: 'If any beneficiary under this Will shall contest or attack this Will or any of its provisions...',
    usageCount: 12,
    lastModified: new Date('2024-11-25'),
    createdAt: new Date('2024-03-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'llc_purpose',
    name: 'LLC Purpose Statement',
    category: 'Business',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard LLC purpose clause',
    content: 'The purpose of the Company is to engage in any lawful act or activity for which limited liability companies may be organized...',
    usageCount: 28,
    lastModified: new Date('2024-12-01'),
    createdAt: new Date('2024-04-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'indemnification',
    name: 'Indemnification Clause',
    category: 'Business',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard member/manager indemnification',
    content: 'The Company shall indemnify and hold harmless each Member and Manager from and against any and all claims...',
    usageCount: 35,
    lastModified: new Date('2024-11-29'),
    createdAt: new Date('2024-04-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // NVCA Clauses
  {
    id: 'nvca-protective-provisions-major',
    name: 'Protective Provisions - Major',
    category: 'NVCA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Investor-friendly protective provisions requiring preferred stock approval for major corporate actions',
    content: `So long as any shares of Series [_] Preferred Stock are outstanding, the Company shall not, without first obtaining the approval (by vote or written consent) of the holders of at least [a majority/____%] of the then outstanding shares of Series [_] Preferred Stock, voting as a separate class:

(a) amend, alter, or repeal any provision of the Certificate of Incorporation or Bylaws of the Company in a manner that adversely affects the powers, preferences or rights of the Series [_] Preferred Stock;

(b) increase or decrease the authorized number of shares of Series [_] Preferred Stock;

(c) authorize or create (by reclassification or otherwise) any new class or series of capital stock having rights, preferences or privileges senior to or on a parity with the Series [_] Preferred Stock;

(d) redeem or repurchase any shares of Common Stock or Preferred Stock (other than pursuant to employee agreements or the Right of First Refusal);

(e) declare or pay any dividend or otherwise make a distribution to holders of Common Stock or Preferred Stock;

(f) increase or decrease the authorized size of the Company's Board of Directors;

(g) result in a Deemed Liquidation Event or any merger or consolidation of the Company;

(h) incur or guarantee any indebtedness in excess of $[_____], other than equipment financing and trade accounts payable arising in the ordinary course of business.`,
    usageCount: 40,
    lastModified: new Date('2024-12-01'),
    createdAt: new Date('2024-06-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'nvca-anti-dilution-broad-based',
    name: 'Anti-Dilution - Broad-Based Weighted Average',
    category: 'NVCA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Balanced anti-dilution protection using broad-based weighted average formula',
    content: `In the event the Company issues Additional Shares of Common Stock at a purchase price less than the current Series [_] Conversion Price, such Series [_] Conversion Price shall be reduced, concurrently with such issue, to a price (calculated to the nearest one-hundredth of a cent) determined in accordance with the following formula:

CP2 = CP1 * (A + B) / (A + C)

Where:
CP2 = Series [_] Conversion Price in effect immediately after new issue
CP1 = Series [_] Conversion Price in effect immediately prior to new issue
A = Number of shares of Common Stock deemed to be outstanding immediately prior to new issue
B = Aggregate consideration received or deemed to be received by the Company for the new issue, divided by CP1
C = Number of shares of stock issued in the subject transaction

The above formula shall be known as the "broad-based weighted average" anti-dilution adjustment.`,
    usageCount: 70,
    lastModified: new Date('2024-12-02'),
    createdAt: new Date('2024-06-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'nvca-liquidation-preference',
    name: 'Liquidation Preference - Non-Participating',
    category: 'NVCA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Company-friendly non-participating liquidation preference',
    content: `In the event of any Liquidation Event, the holders of Series [_] Preferred Stock shall be entitled to receive, prior and in preference to any distribution to the holders of Common Stock, an amount equal to [1x] the Original Issue Price plus any declared but unpaid dividends. After payment of the Liquidation Preference, the remaining assets shall be distributed to the holders of Common Stock.`,
    usageCount: 35,
    lastModified: new Date('2024-12-01'),
    createdAt: new Date('2024-06-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'nvca-drag-along',
    name: 'Drag-Along Rights',
    category: 'NVCA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Investor-friendly drag-along rights for sale of company',
    content: `If (i) the holders of [a majority] of the Series [_] Preferred Stock and (ii) the Board of Directors approve a Sale of the Company, then each Stockholder and the Company shall take all actions necessary to consummate such Sale of the Company, including voting in favor of and not exercising any dissenters' rights with respect to such transaction.`,
    usageCount: 65,
    lastModified: new Date('2024-12-03'),
    createdAt: new Date('2024-06-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // ILPA Clauses
  {
    id: 'ilpa-management-fee',
    name: 'Management Fee',
    category: 'ILPA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Standard private equity fund management fee structure',
    content: `The Management Fee shall be calculated and payable quarterly in advance based on:
- During the Investment Period: [1.5-2.0]% per annum of aggregate Committed Capital
- After the Investment Period: [1.5-2.0]% per annum of invested capital (cost basis of unrealized investments)

The Management Fee shall be reduced by [100]% of any transaction fees, monitoring fees, directors' fees, and other fees received by the General Partner or its affiliates from Portfolio Companies.`,
    usageCount: 42,
    lastModified: new Date('2024-12-01'),
    createdAt: new Date('2024-06-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ilpa-carried-interest',
    name: 'Carried Interest',
    category: 'ILPA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Standard carried interest waterfall with preferred return and catch-up',
    content: `The General Partner shall be entitled to receive as its share of profits (Carried Interest) [20]% of the net profits of the Partnership, subject to the following:

(a) The Preferred Return to Limited Partners of [8]% per annum, compounded annually, must first be achieved;

(b) A [100]% GP Catch-Up until the General Partner has received [20]% of cumulative profits;

(c) Thereafter, [80]% to Limited Partners and [20]% to General Partner.`,
    usageCount: 55,
    lastModified: new Date('2024-12-02'),
    createdAt: new Date('2024-06-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ilpa-key-person',
    name: 'Key Person Clause',
    category: 'ILPA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Investment period suspension upon key person departure',
    content: `In the event that any Key Person ceases to devote substantially all of their business time and attention to the Partnership, the Investment Period shall be automatically suspended. The Advisory Committee may, by [majority/super-majority] vote, either:

(a) Terminate the Investment Period permanently;
(b) Approve a replacement Key Person and reinstate the Investment Period; or
(c) Continue the suspension pending resolution.

Key Persons: [Names to be specified]`,
    usageCount: 38,
    lastModified: new Date('2024-12-01'),
    createdAt: new Date('2024-06-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ilpa-no-fault-divorce',
    name: 'No-Fault Divorce / Early Termination',
    category: 'ILPA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'LP rights to remove GP without cause',
    content: `Upon the affirmative vote of Limited Partners holding at least [80]% of the Capital Commitments, the Limited Partners may, without cause, remove the General Partner and terminate the Investment Period. In such event:

(a) The General Partner shall be entitled to Carried Interest on investments made prior to removal, subject to the normal waterfall provisions;

(b) A successor General Partner may be appointed by vote of Limited Partners holding [66 2/3]% of Capital Commitments;

(c) The Partnership may continue with the successor General Partner or enter an orderly wind-down.`,
    usageCount: 28,
    lastModified: new Date('2024-11-30'),
    createdAt: new Date('2024-06-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ilpa-lpac-composition',
    name: 'LPAC Composition and Authority',
    category: 'ILPA',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Limited Partner Advisory Committee structure and powers',
    content: `The Limited Partner Advisory Committee (LPAC) shall consist of representatives of [5-9] Limited Partners, selected to ensure diverse representation. The LPAC shall have authority to:

(a) Review and approve conflicts of interest and related party transactions;

(b) Review valuation methodologies and specific valuations upon request;

(c) Approve amendments to the Partnership Agreement that do not require LP consent;

(d) Grant or withhold consent for co-investment allocation methodologies.

LPAC members shall owe no fiduciary duties to other Limited Partners by virtue of their LPAC service.`,
    usageCount: 32,
    lastModified: new Date('2024-12-03'),
    createdAt: new Date('2024-06-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Y Combinator SAFE Clauses
  {
    id: 'yc-safe-valuation-cap',
    name: 'SAFE - Valuation Cap (Post-Money)',
    category: 'YC SAFE',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Y Combinator post-money SAFE with valuation cap, no discount',
    content: `SAFE (Simple Agreement for Future Equity)

THIS CERTIFIES THAT in exchange for the payment by [Investor Name] (the "Investor") of $[_____] (the "Purchase Amount") on or about [Date], [Company Name], a Delaware corporation (the "Company"), issues to the Investor the right to certain shares of the Company's Capital Stock, subject to the terms described below.

This Safe is one of the forms available at ycombinator.com/documents.

1. Events
(a) Equity Financing. If there is an Equity Financing before the termination of this Safe, on the initial closing of such Equity Financing, this Safe will automatically convert into the number of shares of Safe Preferred Stock equal to the Purchase Amount divided by the Conversion Price.

(b) Liquidity Event. If there is a Liquidity Event before the termination of this Safe, this Safe will automatically be entitled to receive a portion of Proceeds equal to the greater of (i) the Purchase Amount or (ii) the amount payable on the number of shares of Common Stock equal to the Purchase Amount divided by the Liquidity Price.

2. Definitions
"Conversion Price" means the either: (1) the Safe Price or (2) the Discount Price, whichever calculation results in a greater number of shares of Safe Preferred Stock.

"Post-Money Valuation Cap" means $[_____].

"Safe Price" means the price per share equal to the Post-Money Valuation Cap divided by the Company Capitalization.`,
    usageCount: 85,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-07-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'yc-safe-discount',
    name: 'SAFE - Discount (No Valuation Cap)',
    category: 'YC SAFE',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Y Combinator SAFE with discount rate, no valuation cap',
    content: `SAFE (Simple Agreement for Future Equity) - Discount Only

THIS CERTIFIES THAT in exchange for the payment by [Investor Name] (the "Investor") of $[_____] (the "Purchase Amount"), [Company Name], a Delaware corporation (the "Company"), issues to the Investor the right to certain shares of the Company's Capital Stock, subject to the terms described below.

1. Events
(a) Equity Financing. If there is an Equity Financing before the termination of this Safe, on the initial closing of such Equity Financing, this Safe will automatically convert into the number of shares of Safe Preferred Stock equal to the Purchase Amount divided by the Discount Price.

2. Definitions
"Discount Price" means the lowest price per share of the Standard Preferred Stock sold in the Equity Financing multiplied by the Discount Rate.

"Discount Rate" means [100 minus the Discount]% (e.g., for a 20% discount, the Discount Rate would be 80%).

Standard discount rates: 15%, 20%, or 25%.`,
    usageCount: 42,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-07-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'yc-safe-mfn',
    name: 'SAFE - Most Favored Nation (Uncapped)',
    category: 'YC SAFE',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Y Combinator MFN SAFE - most founder-friendly, no cap or discount',
    content: `SAFE (Simple Agreement for Future Equity) - MFN

THIS CERTIFIES THAT in exchange for the payment by [Investor Name] (the "Investor") of $[_____] (the "Purchase Amount"), [Company Name], a Delaware corporation (the "Company"), issues to the Investor the right to certain shares of the Company's Capital Stock, subject to the terms described below.

1. MFN Amendment Provision
If the Company issues any Subsequent Convertible Securities prior to the termination of this Safe, the Investor will be entitled to receive such Subsequent Convertible Securities in exchange for this Safe upon the same terms and conditions as the holders of such Subsequent Convertible Securities.

"Subsequent Convertible Securities" means any convertible securities that the Company may issue after the date of this Safe, including but not limited to other Safes, convertible promissory notes, and similar instruments.

This is the most founder-friendly SAFE form as it contains neither a valuation cap nor a discount, but provides the investor MFN protection.`,
    usageCount: 28,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-07-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'yc-safe-pro-rata',
    name: 'SAFE - Pro Rata Side Letter',
    category: 'YC SAFE',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Pro rata rights side letter for SAFE investors',
    content: `PRO RATA SIDE LETTER

[Company Name]
[Date]

Dear [Investor Name]:

This letter agreement (this "Side Letter") is being delivered to you in connection with the Safe dated [___] between you and [Company Name] (the "Company") for a purchase amount of $[___] (the "Safe").

Pro Rata Right. The Company hereby grants Investor the right to purchase its pro rata share of Standard Preferred Stock sold in the Equity Financing (the "Pro Rata Right"). "Pro rata share" means the ratio of (a) the number of shares of Common Stock that may be issued upon conversion of this Safe to (b) the Company Capitalization.

To exercise its Pro Rata Right, the Investor must deliver written notice within [15] days after receipt of notice of the Equity Financing from the Company.

This Pro Rata Right shall terminate upon the earlier of (i) the IPO, (ii) a Change of Control, or (iii) the closing of the Equity Financing if the Investor fails to exercise its Pro Rata Right.`,
    usageCount: 38,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-07-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Cooley GO / Series Seed Clauses
  {
    id: 'series-seed-preferred-stock',
    name: 'Series Seed - Preferred Stock Terms',
    category: 'Series Seed',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Standard Series Seed preferred stock terms (Cooley GO)',
    content: `SERIES SEED PREFERRED STOCK - KEY TERMS

1. DIVIDEND RIGHTS
The holders of Series Seed Preferred Stock shall be entitled to receive dividends when, as and if declared by the Board of Directors, in preference and priority to any dividends on Common Stock.

2. LIQUIDATION PREFERENCE
In the event of any liquidation, dissolution or winding up of the Company, the holders of Series Seed Preferred Stock shall be entitled to receive, prior to any distribution to the holders of Common Stock, an amount per share equal to the greater of:
(a) the Original Issue Price of such share of Series Seed Preferred Stock, plus any declared but unpaid dividends; or
(b) the amount such holder would receive if all shares of Series Seed Preferred Stock were converted to Common Stock immediately prior to such liquidation event.

3. CONVERSION RIGHTS
Each share of Series Seed Preferred Stock shall be convertible into shares of Common Stock at the election of the holder at any time. The initial conversion ratio shall be 1:1, subject to adjustment for stock splits, combinations, and similar events.

4. AUTOMATIC CONVERSION
All shares of Series Seed Preferred Stock shall automatically convert into Common Stock upon:
(a) the closing of a Qualified IPO; or
(b) the written consent of the holders of a majority of the then outstanding shares of Series Seed Preferred Stock.`,
    usageCount: 55,
    lastModified: new Date('2024-12-04'),
    createdAt: new Date('2024-07-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'series-seed-voting-rights',
    name: 'Series Seed - Voting Rights',
    category: 'Series Seed',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Voting rights provisions for Series Seed financing',
    content: `SERIES SEED VOTING RIGHTS

1. GENERAL VOTING RIGHTS
Each holder of Series Seed Preferred Stock shall be entitled to the number of votes equal to the number of shares of Common Stock into which such shares of Series Seed Preferred Stock could be converted. The holders of Series Seed Preferred Stock shall vote together with holders of Common Stock as a single class.

2. PROTECTIVE PROVISIONS
For so long as any shares of Series Seed Preferred Stock remain outstanding, the Company shall not, without the prior written consent of the holders of a majority of the Series Seed Preferred Stock:

(a) Amend, alter or repeal any provision of the Certificate of Incorporation or Bylaws in a manner that adversely affects the rights of the Series Seed Preferred Stock;

(b) Increase or decrease the authorized number of shares of Series Seed Preferred Stock;

(c) Create any new class or series of capital stock having rights senior to or on parity with the Series Seed Preferred Stock;

(d) Consummate a Deemed Liquidation Event;

(e) Incur indebtedness in excess of $[____] other than equipment financing or bank lines of credit;

(f) Make any loan or advance to any employee, officer, director, or stockholder.`,
    usageCount: 48,
    lastModified: new Date('2024-12-04'),
    createdAt: new Date('2024-07-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'cooley-convertible-note',
    name: 'Convertible Promissory Note',
    category: 'Cooley GO',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Standard convertible promissory note for seed financing',
    content: `CONVERTIBLE PROMISSORY NOTE

$[Principal Amount]                                    [Date]

FOR VALUE RECEIVED, [Company Name], a Delaware corporation (the "Company"), promises to pay to [Investor Name] or its registered assigns (the "Holder"), the principal sum of [$ Amount] Dollars, together with interest thereon as set forth below.

1. INTEREST RATE
This Note shall bear simple interest at the rate of [8]% per annum.

2. MATURITY DATE
Unless earlier converted, the outstanding principal balance and accrued interest shall be due and payable on [Date 18-24 months from issuance] (the "Maturity Date").

3. CONVERSION
(a) Qualified Financing Conversion. Upon the closing of a Qualified Financing (defined as an equity financing with gross proceeds of at least $[1,000,000]), the outstanding principal and accrued interest shall automatically convert into shares of the preferred stock sold in such financing at a price per share equal to the lesser of:
(i) [80]% of the price per share paid by other investors (the "Discount Price"); or
(ii) the price per share implied by dividing $[Valuation Cap] by the Company's fully-diluted capitalization immediately prior to such financing (the "Cap Price").

(b) Voluntary Conversion. At any time prior to the Maturity Date, Holder may convert this Note into shares of Common Stock at a conversion price implied by a $[Valuation Cap] valuation.

4. SUBORDINATION
This Note is subordinated to all senior indebtedness of the Company.`,
    usageCount: 62,
    lastModified: new Date('2024-12-04'),
    createdAt: new Date('2024-07-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'cooley-ciia',
    name: 'Confidential Information and Invention Assignment',
    category: 'Cooley GO',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Employee CIIA agreement (Cooley GO standard)',
    content: `CONFIDENTIAL INFORMATION AND INVENTION ASSIGNMENT AGREEMENT

This Agreement is entered into by and between [Company Name] (the "Company") and the undersigned employee ("Employee").

1. CONFIDENTIAL INFORMATION
Employee agrees to hold in confidence and not disclose or use, except for the benefit of the Company, any Confidential Information of the Company.

"Confidential Information" means any non-public information relating to the Company's business, including but not limited to: trade secrets, inventions, technical data, product plans, customer lists, financial information, business strategies, and software.

2. INVENTION ASSIGNMENT
Employee hereby assigns to the Company all right, title and interest in and to any and all inventions, discoveries, improvements, and works of authorship that:
(a) relate to the Company's business or actual or demonstrably anticipated research or development; or
(b) result from any work performed by Employee for the Company; or
(c) are developed using Company equipment, supplies, or facilities.

3. PRIOR INVENTIONS
Attached as Exhibit A is a list of all prior inventions that Employee wishes to exclude from this Agreement.

4. NON-SOLICITATION
For [12] months following termination, Employee shall not solicit any employees or consultants of the Company.

5. RETURN OF MATERIALS
Upon termination, Employee shall return all Company property and Confidential Information.`,
    usageCount: 75,
    lastModified: new Date('2024-12-04'),
    createdAt: new Date('2024-07-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'cooley-mutual-nda',
    name: 'Mutual Non-Disclosure Agreement',
    category: 'Cooley GO',
    jurisdictions: ['DE', 'WI', 'FL'],
    description: 'Standard mutual NDA for business discussions',
    content: `MUTUAL NON-DISCLOSURE AGREEMENT

This Mutual Non-Disclosure Agreement (this "Agreement") is entered into as of [Date] by and between [Party A] and [Party B] (each a "Party" and together the "Parties").

1. PURPOSE
The Parties wish to explore a potential business relationship (the "Purpose"). In connection with the Purpose, each Party may disclose Confidential Information to the other Party.

2. CONFIDENTIAL INFORMATION
"Confidential Information" means any non-public information disclosed by either Party to the other Party, either directly or indirectly, in writing, orally, or by inspection, that is designated as confidential or that reasonably should be understood to be confidential given the nature of the information.

3. OBLIGATIONS
Each Party agrees to:
(a) Use the other Party's Confidential Information solely for the Purpose;
(b) Not disclose Confidential Information to any third party without prior written consent;
(c) Protect Confidential Information using at least the same degree of care used to protect its own confidential information, but in no event less than reasonable care;
(d) Limit access to Confidential Information to employees and contractors with a need to know.

4. EXCEPTIONS
Confidential Information does not include information that:
(a) Was publicly known at the time of disclosure;
(b) Becomes publicly known through no fault of the receiving Party;
(c) Was rightfully in the receiving Party's possession prior to disclosure;
(d) Is independently developed without use of Confidential Information.

5. TERM
This Agreement shall remain in effect for [2] years from the Effective Date. Obligations of confidentiality shall survive for [3] years following any disclosure.`,
    usageCount: 88,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-07-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Additional Estate Planning - ACTEC-Style Clauses
  {
    id: 'actec-spendthrift-trust',
    name: 'Spendthrift Trust Provision',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'ACTEC-style spendthrift clause for asset protection',
    content: `SPENDTHRIFT PROVISION

No beneficiary shall have the right to anticipate, sell, assign, mortgage, pledge, or otherwise dispose of or encumber all or any part of his or her interest in income or principal under this trust, whether before or after distribution.

No interest of any beneficiary shall be subject to claims of creditors or others, or to legal process, and shall not be subject to attachment, garnishment, execution, or other process, and shall be free from the interference or control of creditors or others.

The Trustee shall not pay any part of the income or principal of this trust directly to any assignee, creditor, or other claimant of any beneficiary. Any attempted alienation of any interest by a beneficiary, or any attachment, garnishment, or execution thereon, shall be void.

This provision shall not restrict the exercise of any power of appointment or disclaimer, or restrict distributions made at the Trustee's discretion for the beneficiary's benefit.`,
    usageCount: 45,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'actec-trustee-powers',
    name: 'Comprehensive Trustee Powers',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Comprehensive trustee powers clause based on ACTEC recommendations',
    content: `TRUSTEE POWERS

The Trustee shall have all powers conferred by law and the following additional powers, exercisable in the Trustee's absolute discretion:

A. INVESTMENTS
To invest and reinvest in any property, real or personal, without being limited by any statute or rule of law regarding investments by fiduciaries; to retain any asset originally transferred to the trust; to acquire undivided interests; and to invest in mutual funds, exchange-traded funds, and collective investment vehicles.

B. REAL PROPERTY
To acquire, hold, manage, improve, and sell real property; to lease for any term; to partition; to grant easements; to make improvements; and to raze or erect buildings.

C. BUSINESS INTERESTS
To continue, participate in, or dispose of any business interest; to vote stock; to exercise options and conversion privileges; and to form, reorganize, or dissolve business entities.

D. BORROWING
To borrow money from any source, including from the Trustee in its individual capacity, and to mortgage or pledge trust assets as security.

E. DISTRIBUTIONS
To make distributions in cash or in kind, or partly in each; to allocate specific assets among beneficiaries as the Trustee deems fair and equitable.

F. TAX ELECTIONS
To make any and all tax elections; to value assets; to allocate GST exemption; and to join in tax returns with surviving spouse.

G. EMPLOYMENT
To employ attorneys, accountants, investment advisors, and other agents, and to delegate discretionary powers to such agents.`,
    usageCount: 52,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'actec-disclaimer-trust',
    name: 'Disclaimer Trust Provision',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Credit shelter/disclaimer trust provision for tax planning',
    content: `DISCLAIMER TRUST

A. CREATION
If my spouse disclaims any interest in property passing under this Will or Trust, or if my spouse predeceases me, the disclaimed or lapsed property shall be held in a separate trust (the "Disclaimer Trust" or "Credit Shelter Trust") for my spouse's benefit.

B. DISTRIBUTIONS DURING SPOUSE'S LIFETIME
The Trustee may distribute to or for the benefit of my spouse such amounts of net income and principal as the Trustee, in the Trustee's absolute discretion, determines necessary for my spouse's health, education, maintenance, and support, taking into account other resources available to my spouse.

C. STANDARD
The Trustee shall consider my spouse's accustomed standard of living and may make distributions liberally for my spouse's comfort and welfare. It is my intention that my spouse enjoy the same standard of living as during my lifetime.

D. 5 AND 5 POWER
My spouse shall have the noncumulative right to withdraw annually the greater of $5,000 or 5% of the principal of this trust, exercisable by written notice to the Trustee.

E. DISPOSITION AT SPOUSE'S DEATH
Upon the death of my spouse, the remaining trust property shall be distributed to my descendants, per stirpes, or as my spouse may appoint by specific reference to this power in my spouse's will (limited to my descendants and their spouses).

F. TAX INTENT
This trust is intended to qualify for the federal estate tax marital deduction to the extent it does not utilize my available applicable exclusion amount.`,
    usageCount: 38,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Model Real Estate Clauses
  {
    id: 're-wb11-inspection',
    name: 'WI WB-11 Inspection Contingency',
    category: 'Real Estate',
    jurisdictions: ['WI'],
    description: 'Wisconsin WB-11 style inspection contingency',
    content: `INSPECTION CONTINGENCY (Wisconsin WB-11 Style)

This offer is contingent upon Buyer's approval of a professional inspection of the Property. Buyer shall have [10] business days from acceptance to:

(a) Obtain, at Buyer's expense, a written inspection report from a licensed home inspector examining the Property's structural, mechanical, electrical, plumbing, and other systems;

(b) Deliver written notice to Seller identifying defects that Buyer finds unacceptable.

SELLER'S RESPONSE: Within [__] business days of receipt of Buyer's notice, Seller shall respond in writing with one of the following:
(1) Agreement to repair or remedy the identified defects at Seller's expense prior to closing;
(2) Refusal to repair, in which case Buyer may within [__] business days elect to waive the contingency or terminate this offer.

REPAIR STANDARDS: All repairs shall be performed in a workmanlike manner by licensed contractors where required, and Seller shall provide copies of all paid invoices and applicable permits.

WALK-THROUGH: Buyer shall have the right to conduct a walk-through inspection within 3 days prior to closing to verify repairs and condition of Property.

TIME IS OF THE ESSENCE with respect to all deadlines under this contingency.`,
    usageCount: 42,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 're-farbar-as-is',
    name: 'FL FAR/BAR As-Is Clause',
    category: 'Real Estate',
    jurisdictions: ['FL'],
    description: 'Florida FAR/BAR As-Is sale provision',
    content: `AS-IS PROVISION (Florida FAR/BAR Style)

BUYER ACCEPTS THE PROPERTY IN ITS PRESENT "AS IS" CONDITION, subject only to the following:

(a) SELLER'S DISCLOSURE: Seller shall disclose to Buyer all facts known to Seller materially affecting the value of the Property which are not readily observable.

(b) INSPECTION PERIOD: Buyer shall have [15] days from the Effective Date (the "Inspection Period") to have the Property inspected at Buyer's expense by a licensed inspector or other person(s) of Buyer's choice.

(c) BUYER'S SOLE OPTION: If Buyer, in Buyer's sole discretion, determines that the Property is not acceptable, Buyer may cancel this Contract by delivering written notice to Seller prior to the expiration of the Inspection Period, and Buyer shall be entitled to a refund of the deposit.

(d) SELLER'S MAINTENANCE OBLIGATION: Seller shall maintain the Property in its present condition, ordinary wear and tear excepted, until closing. Seller shall not make alterations to the Property without Buyer's consent.

(e) RISK OF LOSS: If the Property is damaged by fire or other casualty prior to closing, Buyer may either:
(1) Take the Property as damaged together with insurance proceeds, if any, or
(2) Cancel this Contract and receive a refund of the deposit.

BUYER ACKNOWLEDGES THAT SELLER MAKES NO WARRANTIES OR REPRESENTATIONS CONCERNING THE CONDITION OF THE PROPERTY.`,
    usageCount: 58,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 're-financing-contingency',
    name: 'Financing Contingency',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard mortgage financing contingency',
    content: `FINANCING CONTINGENCY

This offer is contingent upon Buyer obtaining a written commitment for a mortgage loan secured by the Property on the following terms:

LOAN AMOUNT: $[____] (or [___]% of purchase price)
LOAN TYPE: ☐ Conventional ☐ FHA ☐ VA ☐ Other: _______
INTEREST RATE: Not to exceed [___]% per annum (☐ fixed ☐ adjustable)
TERM: [30] years
POINTS: Not to exceed [___] points

BUYER'S OBLIGATIONS:
(a) Buyer shall make application for such financing within [5] business days of acceptance.
(b) Buyer shall diligently pursue the financing application and timely provide all required documentation.
(c) Buyer shall accept any commitment meeting these terms.

COMMITMENT DEADLINE: Buyer shall obtain and deliver to Seller a written financing commitment on or before [Date].

FAILURE TO OBTAIN COMMITMENT:
If Buyer, despite good faith efforts, is unable to obtain such commitment by the deadline:
(a) Buyer shall promptly notify Seller in writing;
(b) This Contract shall be null and void;
(c) All deposit monies shall be returned to Buyer.

SATISFACTION: This contingency shall be deemed satisfied if Buyer fails to deliver timely notice of inability to obtain financing.

WAIVER: Buyer may waive this contingency at any time by written notice to Seller.`,
    usageCount: 72,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 're-title-insurance',
    name: 'Title Insurance and Examination',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Title examination and insurance provisions',
    content: `TITLE INSURANCE AND EXAMINATION

A. TITLE EVIDENCE
Seller shall, within [___] days of acceptance, provide Buyer with one of the following forms of title evidence:
☐ Owner's title insurance commitment from a licensed title insurer
☐ Abstract of title certified to the Effective Date, to be examined by Buyer's attorney

B. TITLE EXAMINATION PERIOD
Buyer shall have [___] days from receipt of title evidence to examine and notify Seller in writing of any title defects or objections.

C. MARKETABLE TITLE
Seller shall convey marketable title, free and clear of all liens, encumbrances, easements, restrictions, and other exceptions other than:
(a) Zoning and building ordinances
(b) Recorded easements for utilities and drainage
(c) Recorded covenants and restrictions of record
(d) Real property taxes not yet due and payable
(e) [Specific permitted exceptions]

D. CURE OF DEFECTS
If Buyer timely objects to title defects, Seller shall have [___] days to cure such defects at Seller's expense. If Seller is unable or unwilling to cure such defects, Buyer may:
(1) Accept title as it is, without reduction in purchase price; or
(2) Terminate this Contract and receive a refund of all deposits.

E. TITLE INSURANCE POLICY
At closing, Seller shall provide Buyer with an owner's policy of title insurance in the amount of the purchase price, with standard exceptions deleted where possible, insuring marketable title in Buyer.`,
    usageCount: 65,
    lastModified: new Date('2024-12-05'),
    createdAt: new Date('2024-08-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Westlaw-Style Practice Forms - Litigation
  {
    id: 'wl-complaint-negligence',
    name: 'Complaint - General Negligence',
    category: 'Litigation',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard negligence complaint based on Westlaw practice forms',
    content: `COMPLAINT FOR NEGLIGENCE

COMES NOW the Plaintiff, {{plaintiff.name}}, by and through undersigned counsel, and for Plaintiff's Complaint against Defendant, {{defendant.name}}, states and alleges as follows:

PARTIES

1. Plaintiff, {{plaintiff.name}}, is a resident of {{plaintiff.address.county}} County, {{plaintiff.address.state}}.

2. Defendant, {{defendant.name}}, is {{#if defendant.corporation}}a corporation organized under the laws of {{defendant.state}} with its principal place of business in {{defendant.address.county}} County, {{defendant.address.state}}{{#else}}a resident of {{defendant.address.county}} County, {{defendant.address.state}}{{/if}}.

JURISDICTION AND VENUE

3. This Court has jurisdiction over this matter pursuant to [statutory citation].

4. Venue is proper in this Court pursuant to [statutory citation].

FACTUAL ALLEGATIONS

5. On or about {{incidentDate}}, at or near {{incidentLocation}}, Defendant owed a duty of care to Plaintiff.

6. Defendant breached that duty by [describe negligent conduct].

7. As a direct and proximate result of Defendant's negligence, Plaintiff suffered injuries including but not limited to: [describe injuries].

8. As a further direct and proximate result of Defendant's negligence, Plaintiff has incurred and will continue to incur medical expenses, lost wages, pain and suffering, and other damages.

COUNT I - NEGLIGENCE

9. Plaintiff incorporates by reference paragraphs 1-8 as if fully set forth herein.

10. Defendant owed a duty of reasonable care to Plaintiff.

11. Defendant breached that duty by failing to exercise reasonable care.

12. Defendant's breach was the direct and proximate cause of Plaintiff's injuries and damages.

13. As a result, Plaintiff has suffered damages in excess of \${{damageThreshold}}.

WHEREFORE, Plaintiff demands judgment against Defendant for:
a. Compensatory damages in an amount to be determined at trial;
b. Costs of this action;
c. Pre-judgment and post-judgment interest;
d. Such other relief as the Court deems just and proper.

JURY DEMAND

Plaintiff demands trial by jury on all issues so triable.`,
    usageCount: 88,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-answer-affirmative-defenses',
    name: 'Answer with Affirmative Defenses',
    category: 'Litigation',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard answer with affirmative defenses per Westlaw forms',
    content: `ANSWER AND AFFIRMATIVE DEFENSES

COMES NOW Defendant, {{defendant.name}}, by and through undersigned counsel, and in response to Plaintiff's Complaint, answers as follows:

ANSWER TO COMPLAINT

1. Defendant admits the allegations contained in Paragraph 1 of the Complaint.

2. Defendant denies the allegations contained in Paragraph 2 of the Complaint.

3. Defendant is without knowledge or information sufficient to form a belief as to the truth of the allegations in Paragraph 3 of the Complaint and therefore denies the same.

[Continue with specific responses to each paragraph]

AFFIRMATIVE DEFENSES

FIRST AFFIRMATIVE DEFENSE (Comparative/Contributory Negligence)
Plaintiff's own negligence contributed to and was a proximate cause of Plaintiff's injuries and damages, if any. Accordingly, any recovery by Plaintiff should be reduced or barred in proportion to Plaintiff's fault.

SECOND AFFIRMATIVE DEFENSE (Assumption of Risk)
Plaintiff voluntarily assumed the risk of injury by engaging in conduct with knowledge of the risks involved.

THIRD AFFIRMATIVE DEFENSE (Failure to Mitigate)
Plaintiff failed to mitigate damages by failing to seek appropriate medical treatment and/or follow medical advice.

FOURTH AFFIRMATIVE DEFENSE (Statute of Limitations)
Plaintiff's claims are barred in whole or in part by the applicable statute of limitations.

FIFTH AFFIRMATIVE DEFENSE (Superseding/Intervening Cause)
Plaintiff's injuries, if any, were caused in whole or in part by superseding or intervening causes for which Defendant is not responsible.

RESERVATION OF AFFIRMATIVE DEFENSES
Defendant reserves the right to assert additional affirmative defenses as they become known through discovery.

WHEREFORE, Defendant requests that the Court:
a. Enter judgment in favor of Defendant;
b. Dismiss Plaintiff's Complaint with prejudice;
c. Award Defendant costs and attorney's fees;
d. Grant such other relief as the Court deems just and proper.`,
    usageCount: 92,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-interrogatories-standard',
    name: 'Standard Interrogatories',
    category: 'Litigation',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard form interrogatories for general litigation',
    content: `PLAINTIFF'S FIRST SET OF INTERROGATORIES TO DEFENDANT

Plaintiff, {{plaintiff.name}}, pursuant to [Rule citation], propounds the following Interrogatories to Defendant, {{defendant.name}}, to be answered under oath within [30] days.

DEFINITIONS

"Document" means any writing, recording, or photograph, including electronic data, and includes the original or any copy.

"Communication" means any oral, written, or electronic transmission of information.

"You" and "Your" refer to the Defendant named above, including agents, employees, and representatives.

INTERROGATORIES

INTERROGATORY NO. 1: State your full legal name, all other names by which you have been known, date of birth, current address, and all addresses for the past five (5) years.

INTERROGATORY NO. 2: Identify all persons having knowledge of facts relevant to this action, and for each person state: (a) full name; (b) address; (c) telephone number; (d) employer; and (e) a summary of the facts known.

INTERROGATORY NO. 3: Identify all expert witnesses you intend to call at trial, and for each expert state: (a) full name and address; (b) qualifications; (c) subject matter of testimony; (d) opinions to be expressed; (e) basis for each opinion; and (f) compensation.

INTERROGATORY NO. 4: Describe in detail the facts and circumstances of the incident giving rise to this lawsuit.

INTERROGATORY NO. 5: Identify all documents relating to the subject matter of this lawsuit, including the author, recipient, date, and present location.

INTERROGATORY NO. 6: State whether you have obtained any statement from any person regarding this matter, and if so, identify such persons and the date, form, and location of each statement.

INTERROGATORY NO. 7: Identify any and all insurance agreements that may provide coverage for the claims in this lawsuit, including carrier, policy number, and coverage limits.

INTERROGATORY NO. 8: Describe any photographs, videos, or recordings related to this matter, including the date taken, the person who took them, and their current location.`,
    usageCount: 76,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-request-production',
    name: 'Request for Production of Documents',
    category: 'Litigation',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard request for production per Westlaw forms',
    content: `PLAINTIFF'S FIRST REQUEST FOR PRODUCTION OF DOCUMENTS

Plaintiff, {{plaintiff.name}}, pursuant to [Rule citation], requests that Defendant, {{defendant.name}}, produce the following documents for inspection and copying within [30] days at the offices of Plaintiff's counsel.

DEFINITIONS

The definitions set forth in Plaintiff's Interrogatories are incorporated herein by reference.

"Relate to" and "relating to" mean pertaining to, referring to, reflecting, describing, evidencing, constituting, or being in any way logically connected.

INSTRUCTIONS

1. If any document is withheld based on privilege, provide a privilege log stating: the date, author, recipient, subject matter, and nature of privilege claimed.

2. Produce documents as kept in the ordinary course of business or organized by category of request.

3. If electronically stored information is requested, produce in native format or reasonably usable form.

REQUESTS FOR PRODUCTION

REQUEST NO. 1: All documents relating to the incident described in Plaintiff's Complaint.

REQUEST NO. 2: All documents identified in your responses to Plaintiff's Interrogatories.

REQUEST NO. 3: All written or recorded statements obtained from any person relating to this matter.

REQUEST NO. 4: All photographs, videos, diagrams, maps, or other visual depictions relating to this matter.

REQUEST NO. 5: All documents relating to your investigation of the incident.

REQUEST NO. 6: All insurance policies providing coverage for claims alleged in this lawsuit.

REQUEST NO. 7: All maintenance, inspection, or repair records relating to [equipment/premises] for the period [date range].

REQUEST NO. 8: All training manuals, policies, procedures, or guidelines applicable to the conduct at issue.

REQUEST NO. 9: All incident or accident reports relating to this matter or similar incidents.

REQUEST NO. 10: All communications between you and any other party relating to this lawsuit.`,
    usageCount: 82,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-motion-summary-judgment',
    name: 'Motion for Summary Judgment',
    category: 'Litigation',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard motion for summary judgment with memorandum',
    content: `MOTION FOR SUMMARY JUDGMENT

Defendant, {{defendant.name}}, by and through undersigned counsel, respectfully moves this Court for entry of summary judgment in Defendant's favor on all claims pursuant to [Rule citation], and states as follows:

I. INTRODUCTION

This motion demonstrates that there is no genuine issue of material fact and that Defendant is entitled to judgment as a matter of law.

II. STATEMENT OF UNDISPUTED MATERIAL FACTS

1. [Statement of fact with citation to record]
2. [Statement of fact with citation to record]
3. [Statement of fact with citation to record]

III. LEGAL STANDARD

Summary judgment is appropriate when "the movant shows that there is no genuine dispute as to any material fact and the movant is entitled to judgment as a matter of law." [Rule citation]. A fact is "material" if it might affect the outcome under the governing law. [Case citation].

IV. ARGUMENT

A. Defendant Is Entitled to Summary Judgment Because [Legal Theory]

[Detailed legal argument with case citations]

B. Alternatively, Defendant Is Entitled to Summary Judgment on [Additional Ground]

[Additional legal argument]

V. CONCLUSION

For the reasons set forth above, Defendant respectfully requests that this Court grant summary judgment in Defendant's favor on all claims.

CERTIFICATE OF COMPLIANCE WITH LOCAL RULES

The undersigned certifies that this motion complies with [local rule requirements].

CERTIFICATE OF SERVICE

I HEREBY CERTIFY that on {{date}}, a true and correct copy of the foregoing was served via [method] on all parties of record.`,
    usageCount: 68,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Westlaw-Style Practice Forms - Family Law
  {
    id: 'wl-dissolution-petition',
    name: 'Petition for Dissolution of Marriage',
    category: 'Family Law',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard divorce/dissolution petition per Westlaw forms',
    content: `PETITION FOR DISSOLUTION OF MARRIAGE

Petitioner, {{petitioner.name}}, petitions this Court for dissolution of marriage from Respondent, {{respondent.name}}, and states:

1. RESIDENCY
{{#if jurisdiction == "FL"}}
Petitioner has been a resident of the State of Florida for at least six (6) months prior to filing this petition.
{{#else}}
Petitioner has been a resident of the State of Wisconsin for at least six (6) months and of {{petitioner.address.county}} County for at least thirty (30) days prior to filing this petition.
{{/if}}

2. DATE AND PLACE OF MARRIAGE
The parties were married on {{marriageDate}} in {{marriageLocation}}.

3. GROUNDS
{{#if jurisdiction == "FL"}}
The marriage is irretrievably broken.
{{#else}}
The marriage is irretrievably broken. There has been a complete breakdown of the marital relationship with no reasonable prospect of reconciliation.
{{/if}}

4. MINOR CHILDREN
{{#if hasChildren}}
The parties have the following minor children of this marriage:
{{#each children}}
- {{this.name}}, born {{this.dateOfBirth}}
{{/each}}

Petitioner requests that the Court enter orders regarding:
☐ Legal custody (decision-making)
☐ Physical placement/timesharing
☐ Child support
☐ Health insurance for the children
{{#else}}
There are no minor children of this marriage.
{{/if}}

5. PROPERTY AND DEBTS
The parties have acquired property and debts during the marriage that should be equitably divided.

6. MAINTENANCE/ALIMONY
{{#if requestMaintenance}}
Petitioner requests an award of maintenance/alimony based on [factors].
{{#else}}
Petitioner waives the right to maintenance/alimony.
{{/if}}

7. RESTORATION OF NAME
{{#if restoreName}}
Petitioner requests that Petitioner's former name of {{formerName}} be restored.
{{/if}}

WHEREFORE, Petitioner respectfully requests that this Court:
a. Dissolve the marriage between the parties;
b. Equitably divide the marital property and debts;
c. Award appropriate relief regarding the minor children;
d. Grant such other relief as is just and proper.`,
    usageCount: 78,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-parenting-plan',
    name: 'Parenting Plan / Custody Agreement',
    category: 'Family Law',
    jurisdictions: ['WI', 'FL'],
    description: 'Comprehensive parenting plan template per Westlaw forms',
    content: `PARENTING PLAN

This Parenting Plan is entered into by:
Father: {{father.name}}
Mother: {{mother.name}}

regarding the following minor children:
{{#each children}}
{{this.name}}, born {{this.dateOfBirth}}
{{/each}}

I. LEGAL CUSTODY / PARENTAL RESPONSIBILITY

{{#if jointLegalCustody}}
☒ Joint legal custody: Both parents shall share decision-making responsibility for:
- Education
- Healthcare
- Religious upbringing
- Extracurricular activities
{{#else}}
☒ Sole legal custody to {{primaryParent}}
{{/if}}

II. PHYSICAL PLACEMENT / TIMESHARING SCHEDULE

A. Regular Schedule:
{{#if equalPlacement}}
Week 1: Children with Mother
Week 2: Children with Father
Exchanges occur on {{exchangeDay}} at {{exchangeTime}} at {{exchangeLocation}}.
{{/if}}

B. Holiday Schedule:
- Thanksgiving: Alternating years (Even years with Father, Odd years with Mother)
- Christmas Eve: [specify]
- Christmas Day: [specify]
- Spring Break: [specify]
- Summer: Each parent shall have [2-4] weeks of vacation time

C. Special Days:
- Mother's Day: With Mother
- Father's Day: With Father
- Children's Birthdays: [specify]
- Parents' Birthdays: [specify]

III. COMMUNICATION

A. Each parent shall have reasonable telephone/video contact with the children during the other parent's placement time.

B. Neither parent shall interfere with or monitor communications between the children and the other parent.

C. Both parents shall have access to school and medical records.

IV. TRANSPORTATION

{{#if sharedTransportation}}
Transportation shall be shared equally, with the receiving parent responsible for pick-up.
{{/if}}

V. DISPUTE RESOLUTION

The parties agree to attempt mediation before filing any motion to modify this plan.

VI. MODIFICATIONS

This plan may be modified only by written agreement of both parties or by court order.`,
    usageCount: 65,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-marital-settlement',
    name: 'Marital Settlement Agreement',
    category: 'Family Law',
    jurisdictions: ['WI', 'FL'],
    description: 'Comprehensive property settlement agreement per Westlaw forms',
    content: `MARITAL SETTLEMENT AGREEMENT

This Agreement is entered into by {{husband.name}} ("Husband") and {{wife.name}} ("Wife") (collectively, the "Parties").

RECITALS

A. The Parties were married on {{marriageDate}}.
B. Irreconcilable differences have arisen, and the Parties desire to settle their property rights and obligations.

AGREEMENT

ARTICLE I - PROPERTY DIVISION

Section 1.1 - Real Property
{{#if maritalHome}}
The marital residence located at {{maritalHome.address}} shall be:
☐ Sold and proceeds divided equally
☐ Awarded to {{maritalHome.awardedTo}}, who shall refinance within [__] days and pay the other party \${{maritalHome.buyout}}
{{/if}}

Section 1.2 - Personal Property
The parties have divided their personal property to their mutual satisfaction. Each shall retain the property currently in their possession.

Section 1.3 - Vehicles
Husband shall retain: {{husband.vehicles}}
Wife shall retain: {{wife.vehicles}}
Each party shall be responsible for any loans on their retained vehicle.

Section 1.4 - Bank Accounts and Investments
[List specific accounts and division]

Section 1.5 - Retirement Accounts
{{#if retirementAccounts}}
The parties' retirement accounts shall be divided as follows:
{{retirementAccounts}}
Any qualified domestic relations order (QDRO) shall be prepared by [specify].
{{/if}}

ARTICLE II - DEBT ALLOCATION

Husband shall be responsible for:
[List debts]

Wife shall be responsible for:
[List debts]

Each party shall indemnify and hold the other harmless from any debts assigned to them.

ARTICLE III - SPOUSAL SUPPORT

{{#if spousalSupport}}
{{payor}} shall pay {{payee}} the sum of \${{amount}} per month for a period of [__] months/years, commencing on {{startDate}}.
{{#else}}
Each party waives any claim to spousal support/alimony from the other.
{{/if}}

ARTICLE IV - GENERAL PROVISIONS

Section 4.1 - Full Disclosure
Each party represents that they have made full disclosure of their assets and liabilities.

Section 4.2 - Independent Counsel
Each party has had the opportunity to consult with independent legal counsel.

Section 4.3 - Entire Agreement
This Agreement constitutes the entire agreement between the parties.

Section 4.4 - Governing Law
This Agreement shall be governed by the laws of {{jurisdiction}}.`,
    usageCount: 72,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-09-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Westlaw-Style Practice Forms - Employment
  {
    id: 'wl-employment-agreement',
    name: 'Employment Agreement - Executive',
    category: 'Employment',
    jurisdictions: ['WI', 'FL'],
    description: 'Executive employment agreement per Westlaw forms',
    content: `EXECUTIVE EMPLOYMENT AGREEMENT

This Employment Agreement ("Agreement") is entered into as of {{effectiveDate}} by and between {{company.name}}, a {{company.state}} corporation ("Company"), and {{executive.name}} ("Executive").

RECITALS

A. Company desires to employ Executive, and Executive desires to be employed by Company, on the terms set forth herein.

AGREEMENT

1. EMPLOYMENT AND DUTIES

1.1 Position. Company employs Executive as {{position}} reporting to {{reportingTo}}.

1.2 Duties. Executive shall devote Executive's full business time and best efforts to the Company. Executive shall not engage in other employment without Company's written consent.

1.3 Location. Executive's principal place of employment shall be {{location}}.

2. TERM

The term of this Agreement shall commence on {{startDate}} and continue for an initial period of [__] years, automatically renewing for successive one-year periods unless terminated pursuant to Section 5.

3. COMPENSATION AND BENEFITS

3.1 Base Salary. Company shall pay Executive an annual base salary of \${{baseSalary}}, payable in accordance with Company's normal payroll practices.

3.2 Annual Bonus. Executive shall be eligible for an annual bonus with a target of [__]% of Base Salary, based on achievement of performance objectives established by the Board.

3.3 Equity. Executive shall receive [stock options/restricted stock units] as set forth in Exhibit A.

3.4 Benefits. Executive shall participate in all benefit plans available to senior executives.

3.5 Vacation. Executive shall receive [__] weeks of paid vacation annually.

3.6 Expense Reimbursement. Company shall reimburse Executive for reasonable business expenses.

4. RESTRICTIVE COVENANTS

4.1 Confidentiality. Executive shall not disclose Confidential Information during or after employment.

4.2 Non-Competition. During employment and for [__] months thereafter, Executive shall not compete with Company within [geographic area].

4.3 Non-Solicitation. For [__] months after termination, Executive shall not solicit Company's employees or customers.

4.4 Non-Disparagement. Neither party shall make disparaging statements about the other.

5. TERMINATION

5.1 For Cause. Company may terminate for Cause (defined as [definition]).

5.2 Without Cause. Either party may terminate without cause upon [__] days' written notice.

5.3 Severance. Upon termination without Cause, Executive shall receive [__] months of base salary continuation.

6. GENERAL PROVISIONS

6.1 Entire Agreement. This Agreement constitutes the entire agreement between the parties.

6.2 Governing Law. This Agreement shall be governed by {{governingLaw}} law.

6.3 Arbitration. Disputes shall be resolved by binding arbitration.`,
    usageCount: 58,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-10-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-non-compete-agreement',
    name: 'Non-Competition Agreement',
    category: 'Employment',
    jurisdictions: ['WI', 'FL'],
    description: 'Employee non-compete agreement per Westlaw forms',
    content: `NON-COMPETITION AND NON-SOLICITATION AGREEMENT

This Agreement is entered into as of {{date}} by {{employee.name}} ("Employee") in favor of {{company.name}} ("Company").

RECITALS

A. Employee is employed by Company in a position where Employee has access to confidential information and customer relationships.

B. As a condition of [employment/continued employment/receipt of consideration], Employee agrees to the restrictions set forth herein.

NOW, THEREFORE, in consideration of [employment/continued employment/\${{consideration}}], Employee agrees:

1. NON-COMPETITION

During Employee's employment and for a period of {{nonCompetePeriod}} following termination for any reason, Employee shall not, directly or indirectly:

(a) Engage in any Competitive Business within the Restricted Area;

(b) Own any interest in, manage, control, participate in, consult with, or render services for any Competitive Business within the Restricted Area;

(c) Become employed by any entity that competes with Company within the Restricted Area in a capacity similar to Employee's position with Company.

"Competitive Business" means any business that [description].

"Restricted Area" means {{restrictedArea}}.

2. NON-SOLICITATION OF CUSTOMERS

For a period of {{nonSolicitPeriod}} following termination, Employee shall not solicit or attempt to solicit any Customer with whom Employee had contact during the last [__] years of employment.

3. NON-SOLICITATION OF EMPLOYEES

For a period of {{nonSolicitPeriod}} following termination, Employee shall not recruit, solicit, or hire any employee of Company, or encourage any employee to leave Company's employment.

4. CONFIDENTIALITY

Employee shall not disclose or use any Confidential Information of Company, except as required for Employee's duties.

5. REASONABLENESS

Employee acknowledges that the restrictions herein are reasonable and necessary to protect Company's legitimate business interests.

6. REMEDIES

Employee acknowledges that monetary damages may be inadequate for breach of this Agreement, and Company shall be entitled to injunctive relief.

7. SEVERABILITY

If any provision is found unenforceable, it shall be modified to be enforceable to the maximum extent permitted.

8. GOVERNING LAW

This Agreement shall be governed by {{governingLaw}} law.`,
    usageCount: 64,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-10-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-severance-agreement',
    name: 'Severance and Release Agreement',
    category: 'Employment',
    jurisdictions: ['WI', 'FL'],
    description: 'Separation agreement with general release per Westlaw forms',
    content: `SEVERANCE AGREEMENT AND GENERAL RELEASE

This Severance Agreement and General Release ("Agreement") is entered into by {{employee.name}} ("Employee") and {{company.name}} ("Company").

RECITALS

A. Employee's employment with Company terminated effective {{terminationDate}}.

B. Company has offered Employee severance benefits in exchange for a general release of claims.

AGREEMENT

1. SEVERANCE BENEFITS

In consideration for Employee's promises herein, Company shall provide:

(a) Severance Pay: \${{severanceAmount}} (equivalent to [__] weeks of base pay), less applicable withholdings, payable [lump sum/over __ weeks];

(b) COBRA Continuation: Company shall pay the employer portion of COBRA premiums for [__] months;

(c) Outplacement Services: [if applicable];

(d) Reference: Company shall provide a neutral reference confirming dates of employment and last position held.

2. GENERAL RELEASE

Employee, for Employee and Employee's heirs, executors, and assigns, hereby releases and forever discharges Company and its officers, directors, employees, agents, parents, subsidiaries, and affiliates from any and all claims, known and unknown, arising out of or in any way related to Employee's employment or termination, including but not limited to:

- Title VII of the Civil Rights Act of 1964
- Age Discrimination in Employment Act (ADEA)
- Americans with Disabilities Act (ADA)
- Family and Medical Leave Act (FMLA)
- Employee Retirement Income Security Act (ERISA)
- State discrimination and employment laws
- Contract and tort claims

3. ADEA WAIVER

Employee acknowledges that:
(a) This release includes claims under ADEA;
(b) Employee has been advised to consult with an attorney;
(c) Employee has [21/45] days to consider this Agreement;
(d) Employee has 7 days to revoke this Agreement after signing;
(e) This release does not apply to claims arising after execution.

4. RETURN OF PROPERTY

Employee has returned all Company property, including equipment, documents, and confidential information.

5. NON-DISPARAGEMENT

Neither party shall make disparaging statements about the other.

6. CONFIDENTIALITY

Employee shall keep the terms of this Agreement confidential.

7. ACKNOWLEDGMENTS

Employee acknowledges that:
- The severance benefits exceed what Employee would otherwise be entitled to receive;
- Employee has had adequate opportunity to review this Agreement;
- Employee is signing this Agreement voluntarily.`,
    usageCount: 56,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-10-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Westlaw-Style Practice Forms - Contracts
  {
    id: 'wl-services-agreement',
    name: 'Professional Services Agreement',
    category: 'Contracts',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard professional services agreement per Westlaw forms',
    content: `PROFESSIONAL SERVICES AGREEMENT

This Professional Services Agreement ("Agreement") is entered into as of {{effectiveDate}} by and between {{client.name}} ("Client") and {{provider.name}} ("Provider").

1. SERVICES

1.1 Scope. Provider shall perform the services described in Exhibit A (the "Services").

1.2 Standard of Care. Provider shall perform the Services in a professional and workmanlike manner, consistent with industry standards.

1.3 Personnel. Provider shall assign qualified personnel to perform the Services.

2. COMPENSATION

2.1 Fees. Client shall pay Provider:
{{#if hourlyRate}}
☐ Hourly Rate: \${{hourlyRate}} per hour
{{/if}}
{{#if fixedFee}}
☐ Fixed Fee: \${{fixedFee}}
{{/if}}
{{#if retainer}}
☐ Monthly Retainer: \${{retainer}}
{{/if}}

2.2 Expenses. Client shall reimburse Provider for pre-approved expenses.

2.3 Invoicing. Provider shall invoice [monthly/upon completion]. Payment due within [30] days.

2.4 Late Payment. Overdue amounts bear interest at [__]% per month.

3. TERM AND TERMINATION

3.1 Term. This Agreement commences on {{effectiveDate}} and continues until [completion of Services/{{termEndDate}}/terminated].

3.2 Termination for Convenience. Either party may terminate upon [30] days' written notice.

3.3 Termination for Cause. Either party may terminate immediately upon material breach not cured within [15] days of notice.

3.4 Effect of Termination. Upon termination, Client shall pay for Services rendered through termination date.

4. INTELLECTUAL PROPERTY

4.1 Work Product. All work product created by Provider in performance of Services shall be owned by [Client/Provider].

4.2 Pre-Existing IP. Each party retains ownership of its pre-existing intellectual property.

5. CONFIDENTIALITY

5.1 Confidential Information. Each party shall maintain the confidentiality of the other's Confidential Information.

5.2 Permitted Disclosures. Disclosure is permitted to employees and contractors with a need to know, or as required by law.

6. REPRESENTATIONS AND WARRANTIES

6.1 Provider Warranties. Provider represents that: (a) it has authority to enter this Agreement; (b) the Services will be performed in a professional manner; (c) the Services will not infringe third-party rights.

6.2 Disclaimer. EXCEPT AS EXPRESSLY SET FORTH HEREIN, PROVIDER DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED.

7. LIMITATION OF LIABILITY

IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. PROVIDER'S TOTAL LIABILITY SHALL NOT EXCEED [fees paid/$ amount].

8. INDEMNIFICATION

Each party shall indemnify the other against third-party claims arising from such party's breach of this Agreement or negligent acts.

9. GENERAL PROVISIONS

9.1 Independent Contractor. Provider is an independent contractor, not an employee.

9.2 Governing Law. This Agreement is governed by {{governingLaw}} law.

9.3 Entire Agreement. This Agreement constitutes the entire agreement between the parties.`,
    usageCount: 85,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-10-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-lease-commercial',
    name: 'Commercial Lease Agreement',
    category: 'Real Estate',
    jurisdictions: ['WI', 'FL'],
    description: 'Commercial lease agreement per Westlaw forms',
    content: `COMMERCIAL LEASE AGREEMENT

This Lease Agreement ("Lease") is entered into as of {{effectiveDate}} by and between {{landlord.name}} ("Landlord") and {{tenant.name}} ("Tenant").

1. PREMISES

Landlord leases to Tenant the premises located at {{premises.address}} (the "Premises"), consisting of approximately {{premises.squareFeet}} square feet.

2. TERM

2.1 Initial Term. The Lease term begins on {{commencementDate}} and ends on {{expirationDate}} ({{termYears}} years).

2.2 Renewal Option. Tenant has the option to renew for [__] additional terms of [__] years each, upon [__] days' written notice.

3. RENT

3.1 Base Rent. Tenant shall pay base rent of \${{monthlyRent}} per month (\${{annualRent}} annually / \${{perSqFt}} per square foot).

3.2 Payment. Rent is due on the first day of each month, in advance.

3.3 Late Fee. A late fee of [__]% applies to rent received after the [__] day of the month.

3.4 Rent Escalation. Base rent shall increase [__]% annually / adjusted to CPI.

4. ADDITIONAL RENT

4.1 Triple Net (NNN). Tenant shall pay Tenant's Proportionate Share ({{proportionateShare}}%) of:
- Real property taxes
- Insurance
- Common area maintenance (CAM)

4.2 Estimated Payments. Tenant shall pay estimated additional rent monthly, with annual reconciliation.

5. SECURITY DEPOSIT

Tenant has deposited \${{securityDeposit}} as security for performance. Landlord may apply the deposit to cure defaults.

6. USE

The Premises shall be used only for {{permittedUse}} and for no other purpose without Landlord's consent.

7. MAINTENANCE AND REPAIRS

7.1 Landlord's Obligations. Landlord shall maintain: structural elements, roof, exterior walls, and building systems (unless damaged by Tenant).

7.2 Tenant's Obligations. Tenant shall maintain: interior, HVAC maintenance, and repairs arising from Tenant's use.

8. ALTERATIONS

Tenant shall not make alterations without Landlord's prior written consent. Approved alterations become Landlord's property unless Landlord requires removal.

9. INSURANCE

9.1 Tenant's Insurance. Tenant shall maintain: (a) Commercial general liability (\${{liabilityLimit}}); (b) Property insurance for Tenant's property; (c) Workers' compensation.

9.2 Waiver of Subrogation. Each party waives subrogation rights against the other.

10. DEFAULT AND REMEDIES

10.1 Events of Default. Default includes: (a) failure to pay rent within [__] days; (b) breach of any provision not cured within [__] days.

10.2 Landlord's Remedies. Upon default, Landlord may: terminate the Lease; re-enter and relet; accelerate rent; pursue damages.`,
    usageCount: 68,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-10-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-asset-purchase',
    name: 'Asset Purchase Agreement',
    category: 'Business',
    jurisdictions: ['WI', 'FL'],
    description: 'Asset purchase agreement for business acquisitions per Westlaw forms',
    content: `ASSET PURCHASE AGREEMENT

This Asset Purchase Agreement ("Agreement") is entered into as of {{effectiveDate}} by and between {{seller.name}} ("Seller") and {{buyer.name}} ("Buyer").

RECITALS

A. Seller is engaged in the business of {{businessDescription}} (the "Business").
B. Seller desires to sell, and Buyer desires to purchase, substantially all assets of the Business.

AGREEMENT

ARTICLE I - PURCHASE AND SALE

1.1 Purchased Assets. Seller agrees to sell and Buyer agrees to purchase the following assets (the "Purchased Assets"):
(a) Inventory
(b) Equipment and machinery
(c) Accounts receivable
(d) Intellectual property
(e) Customer lists and goodwill
(f) Contracts and agreements (Schedule 1.1(f))
(g) Licenses and permits (to extent transferable)

1.2 Excluded Assets. The following are excluded from this sale:
(a) Cash and cash equivalents
(b) [Other excluded assets]

ARTICLE II - PURCHASE PRICE

2.1 Purchase Price. The total purchase price is \${{purchasePrice}}, allocated as set forth in Schedule 2.1.

2.2 Payment. The Purchase Price shall be paid as follows:
(a) \${{deposit}} deposit upon execution
(b) \${{closingPayment}} at Closing
(c) \${{escrowAmount}} held in escrow for [__] months

ARTICLE III - ASSUMED LIABILITIES

3.1 Assumed Liabilities. Buyer assumes the following liabilities:
(a) Obligations under Assigned Contracts arising after Closing
(b) [Other assumed liabilities]

3.2 Excluded Liabilities. Buyer does not assume: (a) liabilities arising before Closing; (b) litigation pending as of Closing; (c) tax liabilities; (d) employee liabilities.

ARTICLE IV - CLOSING

4.1 Closing Date. Closing shall occur on {{closingDate}} or such other date as the parties agree.

4.2 Deliveries. At Closing, Seller shall deliver: (a) Bill of Sale; (b) Assignment and Assumption Agreement; (c) IP Assignment; (d) other transfer documents.

ARTICLE V - REPRESENTATIONS AND WARRANTIES

5.1 Seller's Representations. Seller represents and warrants:
(a) Organization and authority
(b) No conflicts
(c) Title to assets
(d) Financial statements
(e) No undisclosed liabilities
(f) Compliance with laws
(g) Litigation
(h) Taxes

5.2 Buyer's Representations. Buyer represents and warrants:
(a) Organization and authority
(b) Financial capability
(c) No conflicts

ARTICLE VI - INDEMNIFICATION

6.1 Seller's Indemnification. Seller shall indemnify Buyer for breaches of representations and Excluded Liabilities.

6.2 Buyer's Indemnification. Buyer shall indemnify Seller for breaches of representations and Assumed Liabilities.

6.3 Limitations. Indemnification claims subject to: (a) \${{basket}} basket; (b) \${{cap}} cap; (c) [__] month survival period.`,
    usageCount: 52,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-10-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // Additional Estate Planning - Westlaw Style
  {
    id: 'wl-will-simple',
    name: 'Simple Will',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Simple will for married person with children per Westlaw forms',
    content: `LAST WILL AND TESTAMENT OF {{testator.name | uppercase}}

I, {{testator.name}}, of {{testator.address.city}}, {{testator.address.county}} County, {{testator.address.state}}, being of sound mind and memory, do hereby make, publish, and declare this to be my Last Will and Testament, hereby revoking all prior wills and codicils.

ARTICLE I - FAMILY

I am married to {{spouse.name}}. I have the following children:
{{#each children}}
{{this.name}}, born {{this.dateOfBirth}}
{{/each}}

All references to "my spouse" refer to {{spouse.name}}. All references to "my children" include the above-named children and any children born to or adopted by me after the execution of this Will.

ARTICLE II - PAYMENT OF DEBTS AND EXPENSES

I direct my Personal Representative to pay from my residuary estate all my legally enforceable debts, funeral expenses, and costs of administration.

ARTICLE III - SPECIFIC GIFTS

{{#if specificGifts}}
{{#each specificGifts}}
I give {{this.property}} to {{this.beneficiary}}.
{{/each}}
{{#else}}
I make no specific gifts.
{{/if}}

ARTICLE IV - RESIDUARY ESTATE

I give all the rest, residue, and remainder of my estate as follows:

A. If my spouse survives me, I give my entire residuary estate to my spouse.

B. If my spouse does not survive me, I give my residuary estate in equal shares to my children who survive me, per stirpes.

C. If neither my spouse nor any of my children or their descendants survive me, I give my residuary estate to [alternate beneficiary].

ARTICLE V - MINOR BENEFICIARIES

If any beneficiary is under the age of {{distributionAge}} at the time of distribution, the property shall be held in trust until such beneficiary reaches age {{distributionAge}}, with distributions for health, education, maintenance, and support in the Trustee's discretion.

ARTICLE VI - PERSONAL REPRESENTATIVE

I appoint {{personalRepresentative.name}} as Personal Representative of this Will.
{{#if alternatePersonalRepresentative}}
If {{personalRepresentative.name}} is unable or unwilling to serve, I appoint {{alternatePersonalRepresentative.name}} as successor.
{{/if}}

My Personal Representative shall serve without bond and shall have all powers granted by law.

ARTICLE VII - GUARDIAN

{{#if hasMinorChildren}}
I appoint {{guardian.name}} as guardian of any minor children.
{{/if}}

IN WITNESS WHEREOF, I have signed this Will on {{executionDate}}.

_________________________________
{{testator.name}}, Testator

ATTESTATION

We declare that the person who signed this Will, or asked another to sign for them, did so in our presence, and that we believe them to be of sound mind.

_________________________________
Witness 1 Signature
Print Name: ______________________
Address: _________________________

_________________________________
Witness 2 Signature
Print Name: ______________________
Address: _________________________`,
    usageCount: 95,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'wl-irrevocable-trust',
    name: 'Irrevocable Life Insurance Trust (ILIT)',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Irrevocable life insurance trust per Westlaw forms',
    content: `IRREVOCABLE LIFE INSURANCE TRUST AGREEMENT

This Irrevocable Life Insurance Trust Agreement ("Trust Agreement") is made this {{executionDate}} by {{grantor.name}} ("Grantor") and {{trustee.name}} ("Trustee").

ARTICLE I - TRUST CREATION

1.1 Trust Estate. Grantor hereby transfers to Trustee the property listed in Schedule A, to be held, administered, and distributed as provided herein.

1.2 Irrevocability. THIS TRUST IS IRREVOCABLE. Grantor retains no power to alter, amend, revoke, or terminate this Trust.

1.3 Purpose. The primary purpose of this Trust is to own life insurance policies on the life of Grantor and to manage the proceeds for the benefit of the beneficiaries.

ARTICLE II - LIFE INSURANCE

2.1 Acquisition. Trustee may acquire and maintain life insurance policies on the life of Grantor or any other person in whom the Trust has an insurable interest.

2.2 Premiums. Trustee shall pay premiums from Trust income and principal. If insufficient, Trustee shall notify beneficiaries of the opportunity to contribute.

2.3 Policy Rights. Trustee shall have all rights of ownership in the policies, including the right to: change beneficiaries, borrow against cash value, surrender or exchange policies.

ARTICLE III - CRUMMEY WITHDRAWAL RIGHTS

3.1 Withdrawal Right. Upon each contribution to the Trust, each beneficiary shall have the right to withdraw an amount equal to the lesser of:
(a) The beneficiary's pro rata share of such contribution; or
(b) The annual gift tax exclusion amount.

3.2 Notice. Trustee shall notify each beneficiary in writing of their withdrawal right within a reasonable time of each contribution.

3.3 Lapse. The withdrawal right shall lapse if not exercised within [30] days of the contribution. Lapsed amounts shall continue to be held in trust.

ARTICLE IV - DISTRIBUTIONS DURING GRANTOR'S LIFETIME

During Grantor's lifetime, Trustee may distribute income and principal to or for the benefit of [beneficiaries] for health, education, maintenance, and support.

ARTICLE V - DISTRIBUTIONS UPON GRANTOR'S DEATH

5.1 Payment of Expenses. Trustee may, in Trustee's discretion, pay estate taxes, debts, and administration expenses.

5.2 Distribution of Trust Estate. Upon Grantor's death, Trustee shall divide and distribute the Trust estate as follows:
[Specify distribution provisions]

ARTICLE VI - TRUSTEE PROVISIONS

6.1 Successor Trustee. If Trustee ceases to serve, {{successorTrustee.name}} shall serve as successor.

6.2 Powers. Trustee shall have all powers granted by law, including power to invest without limitation, to borrow, and to deal with insurance companies.

6.3 Compensation. Trustee shall be entitled to reasonable compensation.

ARTICLE VII - ADMINISTRATIVE PROVISIONS

7.1 Spendthrift. No beneficiary may anticipate, assign, or encumber their interest.

7.2 Governing Law. This Trust shall be governed by the laws of {{governingLaw}}.

7.3 Severability. Invalid provisions shall not affect the validity of the remainder.`,
    usageCount: 42,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-01'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // =====================================================================
  // COMPREHENSIVE ESTATE PLANNING FORMS AND CLAUSES
  // =====================================================================

  // WILL FORMS AND CLAUSES
  {
    id: 'ep-pour-over-will',
    name: 'Pour-Over Will',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Pour-over will to fund revocable living trust at death',
    content: `POUR-OVER LAST WILL AND TESTAMENT OF {{testator.name | uppercase}}

I, {{testator.name}}, of {{testator.address.city}}, {{testator.address.county}} County, {{testator.address.state}}, being of sound mind, declare this to be my Last Will and Testament, revoking all prior wills and codicils.

ARTICLE I - FAMILY DECLARATION

I am {{#if married}}married to {{spouse.name}}{{#else}}not married{{/if}}.
{{#if children}}
I have the following children:
{{#each children}}
- {{this.name}}, born {{this.dateOfBirth}}
{{/each}}
{{/if}}

ARTICLE II - PAYMENT OF DEBTS

I direct my Personal Representative to pay from my probate estate my legally enforceable debts, funeral expenses, and costs of administration. My Personal Representative may, in their discretion, pay any or all such debts and expenses from the Trust established under Article III.

ARTICLE III - POUR-OVER PROVISION

I give all the rest, residue, and remainder of my estate, both real and personal, of whatever kind and wherever situated, to the then-acting Trustee of the {{trustName | default:"[Testator Name] Revocable Living Trust"}} dated {{trustDate}}, to be added to, administered, and distributed as part of said Trust according to its terms as amended from time to time.

If for any reason this pour-over gift fails or is invalid, I direct that my residuary estate be distributed as if the Trust had terminated at my death, according to the distribution provisions of the Trust as they existed immediately before my death.

ARTICLE IV - POWERS OF PERSONAL REPRESENTATIVE

My Personal Representative shall have all powers conferred by law, including the power to:
(a) Sell, lease, or mortgage any property without court approval;
(b) Continue any business in which I had an interest;
(c) Settle claims and distribute assets in cash or in kind;
(d) Employ attorneys, accountants, and other advisors;
(e) Make elections for tax purposes.

ARTICLE V - PERSONAL REPRESENTATIVE

I appoint {{personalRepresentative.name}} as Personal Representative of this Will.
{{#if alternatePersonalRepresentative}}
If {{personalRepresentative.name}} is unable or unwilling to serve, I appoint {{alternatePersonalRepresentative.name}} as successor Personal Representative.
{{/if}}

My Personal Representative shall serve without bond.

{{#if hasMinorChildren}}
ARTICLE VI - GUARDIAN OF MINOR CHILDREN

I appoint {{guardian.name}} as guardian of the person of my minor children.
{{#if alternateGuardian}}
If {{guardian.name}} is unable or unwilling to serve, I appoint {{alternateGuardian.name}} as successor guardian.
{{/if}}
{{/if}}

IN WITNESS WHEREOF, I have signed this Will on {{executionDate}}.

_________________________________
{{testator.name}}, Testator`,
    usageCount: 65,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-testamentary-trust-clause',
    name: 'Testamentary Trust for Minor Beneficiaries',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Trust provisions within a will for minor or young beneficiaries',
    content: `TESTAMENTARY TRUST FOR MINOR BENEFICIARIES

If any beneficiary under this Will is under the age of {{distributionAge | default:"25"}} at the time they are entitled to receive a distribution, the share of such beneficiary shall not be distributed outright but shall instead be held in a separate trust for such beneficiary according to the following provisions:

A. TRUSTEE
The Trustee of each such trust shall be {{trustee.name}}. If {{trustee.name}} is unable or unwilling to serve, {{alternateTrustee.name}} shall serve as successor Trustee.

B. DISTRIBUTIONS DURING MINORITY
1. The Trustee may distribute to or for the benefit of the beneficiary so much of the net income and principal of the trust as the Trustee determines necessary for the beneficiary's health, education, maintenance, and support.

2. In making distributions, the Trustee may consider:
   - The beneficiary's age, needs, and standard of living
   - Other income and resources available to the beneficiary
   - The desirability of supplementing income from other sources
   - The advisability of accumulating income

3. Any income not distributed shall be accumulated and added to principal.

C. DISTRIBUTION UPON REACHING SPECIFIED AGE
{{#if stagedDistributions}}
The beneficiary shall receive distributions of principal as follows:
- One-third (1/3) of the trust principal upon reaching age {{firstDistributionAge | default:"25"}}
- One-half (1/2) of the remaining principal upon reaching age {{secondDistributionAge | default:"30"}}
- The entire remaining balance upon reaching age {{finalDistributionAge | default:"35"}}
{{#else}}
Upon the beneficiary reaching age {{distributionAge | default:"25"}}, the Trustee shall distribute the entire remaining trust estate to the beneficiary outright and free of trust.
{{/if}}

D. DEATH OF BENEFICIARY BEFORE COMPLETE DISTRIBUTION
If the beneficiary dies before receiving complete distribution, the remaining trust estate shall be distributed:
{{#if beneficiaryHasDescendants}}
To the beneficiary's then-living descendants, per stirpes; or if none,
{{/if}}
To the persons who would have taken had the beneficiary died immediately after the testator, determined as of the date of the beneficiary's death.

E. SPENDTHRIFT PROVISION
No beneficiary shall have the power to anticipate, assign, or encumber any interest in this trust, and no creditor shall have the right to attach such interest prior to actual distribution.`,
    usageCount: 58,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-residuary-clause-spouse-children',
    name: 'Residuary Clause - Spouse and Children',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Standard residuary distribution to spouse then children',
    content: `RESIDUARY ESTATE

I give all the rest, residue, and remainder of my estate, of whatever kind and wherever situated (the "Residuary Estate"), as follows:

A. IF MY SPOUSE SURVIVES ME
If my spouse, {{spouse.name}}, survives me by at least {{survivorshipPeriod | default:"30"}} days, I give my entire Residuary Estate to my spouse.

B. IF MY SPOUSE DOES NOT SURVIVE ME
If my spouse does not survive me by at least {{survivorshipPeriod | default:"30"}} days, I give my Residuary Estate to my children who survive me, in equal shares, per stirpes.

C. PER STIRPES DISTRIBUTION
If any child of mine predeceases me but leaves descendants who survive me, such deceased child's share shall pass to such child's descendants, per stirpes.

D. IF NO SPOUSE OR DESCENDANTS SURVIVE
If neither my spouse nor any descendant of mine survives me, I give my Residuary Estate to:
{{#if alternateResiduary}}
{{alternateResiduary}}
{{#else}}
[To be specified by testator]
{{/if}}

E. SPECIFIC EXCLUSIONS
I have intentionally made no provision for any person not specifically named in this Will. Any such omission is intentional and not the result of accident or mistake.`,
    usageCount: 72,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-specific-bequests',
    name: 'Specific Bequests Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Specific gifts of property, cash, and tangible personal property',
    content: `SPECIFIC BEQUESTS

A. CASH BEQUESTS
I give the following cash bequests:
{{#each cashBequests}}
- \${{this.amount}} to {{this.beneficiary}}{{#if this.purpose}} for the purpose of {{this.purpose}}{{/if}}.
{{/each}}

B. SPECIFIC PROPERTY BEQUESTS
I give the following specific items of property:
{{#each propertyBequests}}
- {{this.description}} to {{this.beneficiary}}.
{{/each}}

C. TANGIBLE PERSONAL PROPERTY
{{#if personalPropertyMemorandum}}
I may leave a written statement or list disposing of items of tangible personal property. Any such writing, if found among my papers at my death, shall be given effect to the extent permitted by law, even though it does not meet the requirements for a will.

If I leave no such writing, or to the extent such writing does not dispose of all my tangible personal property, I give my remaining tangible personal property to {{personalPropertyBeneficiary | default:"my spouse if living, otherwise to my children in equal shares"}}.
{{#else}}
I give all my tangible personal property, including but not limited to furniture, furnishings, household items, jewelry, artwork, collections, automobiles, and other personal effects, to {{personalPropertyBeneficiary}}.
{{/if}}

D. LAPSED BEQUESTS
If any beneficiary of a specific bequest does not survive me, the bequest shall lapse and become part of my residuary estate, unless I have provided an alternate beneficiary.

E. ADEMPTION
If any specifically bequeathed property is not owned by me at my death, the bequest shall be deemed adeemed and the beneficiary shall not be entitled to any substitute gift or compensation, unless I have provided otherwise.`,
    usageCount: 48,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-disinheritance-clause',
    name: 'Disinheritance Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Clause explicitly disinheriting specific individuals',
    content: `DISINHERITANCE

I have intentionally and with full knowledge of the consequences made no provision in this Will for {{disinheritedPerson.name}}, {{disinheritedPerson.relationship}}.

{{#if stateReason}}
The reasons for this decision are personal to me and need not be stated here.
{{/if}}

This omission is deliberate and not the result of accident, inadvertence, or lack of knowledge of {{disinheritedPerson.name}}'s existence. I direct that {{disinheritedPerson.name}} shall receive no part of my estate and shall have no rights therein.

If any court determines that {{disinheritedPerson.name}} is entitled to any share of my estate by law, I direct that such share be limited to the minimum amount required by law.

I have made this decision after careful consideration and independent of any influence from any other person. This provision represents my true intent.`,
    usageCount: 25,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // TRUST CLAUSES AND PROVISIONS
  {
    id: 'ep-ab-trust-marital-bypass',
    name: 'A-B Trust (Marital/Bypass Trust)',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Credit shelter trust structure for married couples',
    content: `DIVISION INTO MARITAL AND BYPASS TRUSTS

Upon the death of the first spouse (the "Deceased Spouse"), the Trustee shall divide the trust estate into two separate trusts:

A. BYPASS TRUST (CREDIT SHELTER TRUST / "B" TRUST)

1. Funding. The Bypass Trust shall be funded with the largest amount that can pass free of federal estate tax by reason of the Deceased Spouse's applicable exclusion amount, reduced by:
   (a) The value of other property passing to persons other than the surviving spouse that qualifies for the marital deduction; and
   (b) Administration expenses and other deductions allocated to this trust.

2. Distributions During Surviving Spouse's Lifetime. The Trustee may distribute to or for the benefit of the surviving spouse and/or the descendants of the Deceased Spouse:
   (a) All net income, at least annually; and
   (b) Such amounts of principal as the Trustee determines advisable for health, education, maintenance, and support.

3. Five and Five Power. The surviving spouse shall have the noncumulative right to withdraw annually the greater of \$5,000 or 5% of the principal of this trust.

4. Distribution at Surviving Spouse's Death. Upon the death of the surviving spouse, the remaining trust estate shall be distributed to {{bypassBeneficiaries | default:"the descendants of the Deceased Spouse, per stirpes"}}.

B. MARITAL TRUST (QTIP TRUST / "A" TRUST)

1. Funding. The Marital Trust shall be funded with the balance of the trust estate not allocated to the Bypass Trust.

2. QTIP Election. The Trustee may elect to treat all or a portion of this trust as qualified terminable interest property (QTIP) for federal estate tax purposes.

3. Distributions During Surviving Spouse's Lifetime.
   (a) The Trustee shall distribute all net income to the surviving spouse at least annually.
   (b) The Trustee may distribute principal to the surviving spouse for health, education, maintenance, and support.

4. Distribution at Surviving Spouse's Death. Upon the death of the surviving spouse, the remaining trust estate shall be distributed to {{maritalBeneficiaries | default:"the descendants of both spouses, per stirpes"}}.

C. TAX ALLOCATION

All estate taxes attributable to the Marital Trust shall be paid from the Marital Trust. All estate taxes attributable to the Bypass Trust shall be paid from the Bypass Trust.`,
    usageCount: 45,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-special-needs-trust',
    name: 'Special Needs Trust (Supplemental Needs Trust)',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Third-party special needs trust for disabled beneficiary',
    content: `SPECIAL NEEDS TRUST FOR {{beneficiary.name | uppercase}}

This Special Needs Trust (also known as a Supplemental Needs Trust) is created for the benefit of {{beneficiary.name}} (the "Beneficiary").

ARTICLE I - PURPOSE AND INTENT

A. The purpose of this Trust is to supplement, not supplant, any government benefits for which the Beneficiary may be eligible. This Trust is intended to provide for the Beneficiary's supplemental and extra needs beyond basic support.

B. It is not the intent of this Trust to provide for the Beneficiary's basic maintenance and support to the extent that such support is available from government benefits programs, including but not limited to Supplemental Security Income (SSI), Medicaid, or other means-tested benefits.

C. This Trust is a third-party special needs trust, funded with assets that have never belonged to the Beneficiary.

ARTICLE II - DISTRIBUTIONS

A. DISCRETIONARY DISTRIBUTIONS
The Trustee, in the Trustee's sole and absolute discretion, may make distributions from income and principal for the Beneficiary's supplemental needs, including but not limited to:
- Education and training
- Recreation and entertainment
- Travel and transportation
- Personal care attendants beyond those provided by government programs
- Medical and dental care not covered by government benefits
- Technology and equipment
- Clothing, furniture, and personal items
- Housing costs not covered by government benefits

B. PROHIBITED DISTRIBUTIONS
The Trustee shall NOT make distributions that would:
- Disqualify the Beneficiary from government benefits;
- Be counted as income or resources for eligibility purposes;
- Duplicate benefits otherwise available from government programs.

C. MANNER OF DISTRIBUTION
Distributions shall be made directly to third parties providing goods or services to the Beneficiary, rather than to the Beneficiary directly, whenever possible.

ARTICLE III - TRUSTEE AUTHORITY

A. The Trustee may consult with the Beneficiary's guardian, family members, and care providers regarding the Beneficiary's needs.

B. The Trustee may decline to make any distribution that the Trustee believes might jeopardize the Beneficiary's eligibility for government benefits.

C. The Trustee may petition a court for guidance regarding any distribution.

ARTICLE IV - TERMINATION

A. This Trust shall terminate upon the earlier of:
- The death of the Beneficiary;
- The Beneficiary no longer being disabled as defined by applicable law; or
- The trust estate being exhausted.

B. Upon termination due to the Beneficiary's death, the remaining trust estate shall be distributed to {{remainderBeneficiaries | default:"the Grantor's then-living descendants, per stirpes"}}.

C. NO PAYBACK PROVISION. Because this is a third-party special needs trust, no reimbursement to any government agency is required upon termination.`,
    usageCount: 38,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-generation-skipping-trust',
    name: 'Generation-Skipping Trust (Dynasty Trust)',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Trust designed to benefit multiple generations while minimizing transfer taxes',
    content: `GENERATION-SKIPPING TRUST

This trust is established to benefit multiple generations of my descendants while minimizing generation-skipping transfer taxes.

ARTICLE I - GST ALLOCATION

A. The Grantor intends that the Grantor's GST exemption be allocated to this trust to the maximum extent available.

B. The Trustee shall keep records sufficient to track the inclusion ratio of this trust.

ARTICLE II - BENEFICIARIES

The beneficiaries of this trust are:
- Primary: My children
- Secondary: My grandchildren and more remote descendants

ARTICLE III - DISTRIBUTIONS

A. DISCRETIONARY DISTRIBUTIONS
The Trustee may distribute income and principal to or among the beneficiaries as the Trustee determines advisable for each beneficiary's health, education, maintenance, and support, taking into account:
- The beneficiary's other resources
- The desirability of maintaining separate trusts for different generations
- Tax efficiency considerations

B. MANDATORY INCOME DISTRIBUTIONS
[Optional] The Trustee shall distribute all net income to [specified beneficiaries] at least annually.

C. GENERATION ALLOCATION
The Trustee should consider the tax efficiency of distributions to skip persons versus non-skip persons.

ARTICLE IV - SEPARATE TRUSTS

A. The Trustee may divide this trust into separate trusts for administrative convenience or to facilitate the allocation of GST exemption.

B. Each separate trust shall be administered according to the same terms as this trust.

ARTICLE V - PERPETUITIES SAVINGS CLAUSE

Notwithstanding any other provision of this trust:

{{#if jurisdiction == "WI"}}
This trust shall terminate no later than {{perpetuitiesPeriod | default:"360"}} years after the date of creation, at which time the remaining trust estate shall be distributed outright to the then-living beneficiaries.
{{#else}}
This trust shall terminate no later than 21 years after the death of the last survivor of all beneficiaries living at the creation of this trust who are descendants of the Grantor, at which time the remaining trust estate shall be distributed outright to the then-living beneficiaries.
{{/if}}

ARTICLE VI - TRUST PROTECTOR

A. {{trustProtector.name}} shall serve as Trust Protector.

B. The Trust Protector shall have the power to:
- Modify administrative provisions to respond to changes in law
- Change the situs of the trust
- Remove and replace the Trustee
- Convert the trust to a grantor trust or non-grantor trust`,
    usageCount: 32,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-pet-trust',
    name: 'Pet Trust',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Trust for the care of pets after owner\'s death or incapacity',
    content: `PET TRUST

I establish this trust for the care of my pets during my incapacity and after my death.

ARTICLE I - PETS COVERED

This trust is established for the care of the following pets:
{{#each pets}}
- {{this.name}}, a {{this.species}}{{#if this.breed}} ({{this.breed}}){{/if}}{{#if this.description}}, {{this.description}}{{/if}}
{{/each}}

This trust shall also cover any pets I acquire after the date of this document.

ARTICLE II - CAREGIVER

A. I designate {{caregiver.name}} as the primary caregiver for my pets.

B. If {{caregiver.name}} is unable or unwilling to serve, I designate {{alternateCaregiver.name}} as successor caregiver.

C. The caregiver shall provide day-to-day care for my pets, including food, shelter, exercise, companionship, and veterinary care.

ARTICLE III - FUNDING

A. This trust shall be funded with \${{trustFunding | default:"[amount]"}}.

B. If the funding is insufficient, the Trustee may petition my estate or trust for additional funds.

ARTICLE IV - TRUSTEE DUTIES

The Trustee shall:
(a) Make regular payments to the caregiver for the costs of caring for my pets, including:
    - Food and treats
    - Veterinary care, including routine and emergency care
    - Grooming
    - Pet supplies
    - Housing modifications if needed
    - Pet insurance premiums

(b) Ensure that my pets receive quality care consistent with the care I provided during my lifetime.

(c) Visit my pets periodically to verify their well-being.

(d) Remove a pet from a caregiver's care if the Trustee determines the care is inadequate.

ARTICLE V - STANDARD OF CARE

I direct that my pets receive the following standard of care:
{{#if careInstructions}}
{{careInstructions}}
{{#else}}
- Regular veterinary checkups at least annually
- Quality food appropriate for each pet's age and health
- Daily exercise and attention
- A clean and comfortable living environment
- Prompt veterinary care for any illness or injury
{{/if}}

ARTICLE VI - TERMINATION

A. This trust shall terminate upon the death of the last surviving pet covered by this trust.

B. Upon termination, any remaining trust funds shall be distributed to {{remainderBeneficiary | default:"[charity or individual]"}}.

ARTICLE VII - NO EUTHANASIA EXCEPT FOR SUFFERING

I direct that my pets not be euthanized except to relieve suffering when no other reasonable option exists. The caregiver and Trustee shall consult with a veterinarian before making any such decision.`,
    usageCount: 22,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-charitable-remainder-trust',
    name: 'Charitable Remainder Unitrust (CRUT)',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Charitable remainder unitrust with income to donor then remainder to charity',
    content: `CHARITABLE REMAINDER UNITRUST AGREEMENT

This Charitable Remainder Unitrust Agreement ("Trust Agreement") is made this {{executionDate}} by {{grantor.name}} ("Grantor" and "Trustee").

ARTICLE I - CREATION OF TRUST

Grantor hereby transfers to the Trustee the property described in Schedule A to be held, administered, and distributed as a charitable remainder unitrust as described in Section 664(d)(2) of the Internal Revenue Code.

ARTICLE II - PAYMENT OF UNITRUST AMOUNT

A. UNITRUST PERCENTAGE
In each taxable year of the Trust, the Trustee shall pay to {{incomeRecipient.name}} (the "Recipient") a unitrust amount equal to {{unitrustPercentage | default:"5"}}% of the net fair market value of the Trust assets, valued as of the first day of each taxable year.

B. PAYMENT SCHEDULE
The unitrust amount shall be paid in [quarterly/annual] installments.

C. PRORATION
The unitrust amount for a short taxable year shall be prorated.

D. INCOME INSUFFICIENT
If the Trust income is insufficient to pay the unitrust amount, the difference shall be paid from principal.

ARTICLE III - TERM

The unitrust amount shall be paid for the {{#if termOfYears}}fixed term of {{termYears}} years{{#else}}lifetime of the Recipient{{/if}}.

ARTICLE IV - DISTRIBUTION TO CHARITY

Upon termination of the unitrust period, the Trustee shall distribute all remaining trust property to {{charityName}}, a qualified organization described in Sections 170(c), 2055(a), and 2522(a) of the Internal Revenue Code.

If {{charityName}} is not a qualified organization at the time of distribution, the Trustee shall distribute the remaining trust property to one or more organizations described in those sections, as the Trustee shall select.

ARTICLE V - ADDITIONAL CONTRIBUTIONS

Additional contributions may be made to this Trust, subject to the Trustee's acceptance. The unitrust amount for the year of contribution shall be recalculated.

ARTICLE VI - PROHIBITED TRANSACTIONS

A. The Trustee shall not engage in any act of self-dealing as defined in Section 4941(d) of the Internal Revenue Code.

B. The Trustee shall not make any taxable expenditure as defined in Section 4945(d).

C. The Trust shall not have any unrelated business taxable income.

ARTICLE VII - TRUSTEE POWERS

The Trustee shall have all powers necessary to administer the Trust, including the power to:
- Invest and reinvest trust property
- Sell, exchange, or otherwise dispose of trust property
- Employ advisors and agents

ARTICLE VIII - IRREVOCABILITY

This Trust is irrevocable. The Grantor reserves no power to alter, amend, revoke, or terminate this Trust.

ARTICLE IX - GOVERNING LAW

This Trust shall be governed by the laws of {{grantor.address.state}} and shall be administered and construed to qualify as a charitable remainder unitrust under Section 664 of the Internal Revenue Code.`,
    usageCount: 28,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-qtip-trust',
    name: 'QTIP Trust Provision',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Qualified Terminable Interest Property trust for surviving spouse',
    content: `QUALIFIED TERMINABLE INTEREST PROPERTY (QTIP) TRUST

ARTICLE I - QTIP ELECTION

The Trustee (or my Personal Representative) may elect to treat all or any portion of this trust as qualified terminable interest property (QTIP) under Internal Revenue Code Section 2056(b)(7) for federal estate tax purposes.

ARTICLE II - MANDATORY INCOME

A. The Trustee shall distribute all net income from this trust to my spouse, {{spouse.name}}, at least annually for my spouse's lifetime.

B. "Net income" means all income as determined under applicable state law, with adjustments as the Trustee deems appropriate for a reasonable apportionment between income and principal beneficiaries.

ARTICLE III - PRINCIPAL DISTRIBUTIONS

A. The Trustee may distribute principal to my spouse for my spouse's health, education, maintenance, and support.

B. In determining distributions, the Trustee may consider my spouse's other income and resources.

C. It is my intent that my spouse maintain the standard of living to which my spouse was accustomed during our marriage.

ARTICLE IV - POWER TO WITHDRAW

My spouse shall have no power to appoint or withdraw principal from this trust except as provided in Article III.

ARTICLE V - DISPOSITION AT SPOUSE'S DEATH

Upon the death of my spouse, the remaining trust principal shall be distributed as follows:
{{#if remainderToDescendants}}
To my descendants, per stirpes, or if none are then living, to {{alternateRemainder}}.
{{#else}}
{{qtipRemainder}}
{{/if}}

ARTICLE VI - TAX PROVISIONS

A. REVERSE QTIP ELECTION
The Trustee may make a reverse QTIP election under Internal Revenue Code Section 2652(a)(3) with respect to all or a portion of this trust.

B. ESTATE TAX PAYMENT
Estate taxes payable by reason of the inclusion of this trust in my spouse's estate shall be paid from {{taxPaymentSource | default:"the assets of this trust"}}.

ARTICLE VII - INCOME IN RESPECT OF DECEDENT

The Trustee shall allocate items constituting income in respect of a decedent (IRD) between the income and principal of this trust in a manner that the Trustee determines is fair and equitable to income and remainder beneficiaries.`,
    usageCount: 42,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // POWER OF ATTORNEY CLAUSES
  {
    id: 'ep-poa-financial-powers',
    name: 'Financial Power of Attorney - Comprehensive Powers',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Detailed enumeration of financial powers for durable POA',
    content: `POWERS GRANTED TO AGENT

I grant my Agent the following powers, to be exercised in my Agent's sole discretion:

A. REAL PROPERTY
To acquire, sell, exchange, lease, encumber, and manage any interest in real property; to execute deeds, mortgages, leases, and other instruments; to pay taxes, assessments, and maintenance costs.

B. PERSONAL PROPERTY
To acquire, sell, exchange, lease, encumber, and manage any tangible or intangible personal property; to execute bills of sale and other instruments of transfer.

C. BANKING AND FINANCIAL INSTITUTIONS
To open, close, and manage accounts at any financial institution; to sign checks, make deposits and withdrawals; to access safe deposit boxes; to obtain credit cards and lines of credit.

D. STOCKS, BONDS, AND SECURITIES
To purchase, sell, exchange, and manage stocks, bonds, mutual funds, and other securities; to vote proxies; to exercise stock options and conversion rights.

E. RETIREMENT PLANS
To create, modify, and terminate retirement accounts including IRAs, 401(k)s, and pension plans; to make contributions, withdrawals, and rollovers; to designate beneficiaries; to make required minimum distributions.

F. INSURANCE
To obtain, modify, and terminate insurance policies of all types; to pay premiums; to submit claims; to receive and negotiate settlement of claims; to designate beneficiaries.

G. TAXES
To prepare, sign, and file federal, state, and local tax returns; to represent me before taxing authorities; to pay taxes and receive refunds; to make elections; to extend deadlines; to settle disputes.

H. GOVERNMENT BENEFITS
To apply for and receive government benefits including Social Security, Medicare, Medicaid, and veterans benefits; to represent me before government agencies.

I. BUSINESS INTERESTS
To conduct business operations; to form, modify, and dissolve business entities; to vote ownership interests; to hire and fire employees; to enter into contracts.

J. GIFTS
{{#if giftingPower}}
To make gifts of my property, including gifts to my Agent, in amounts not exceeding the annual federal gift tax exclusion per donee per year, for the purpose of minimizing taxes or qualifying me for government benefits.
{{#else}}
[Gift-giving power not granted]
{{/if}}

K. TRUSTS
To create, fund, modify, and revoke revocable trusts; to make distributions from trusts where I am trustee; to exercise any powers I have as beneficiary of a trust.

L. ESTATE PLANNING
To create, modify, and revoke beneficiary designations on accounts and insurance policies; to disclaim inheritances; to exercise powers of appointment.

M. LEGAL ACTIONS
To commence, defend, and settle legal proceedings; to retain attorneys; to sign court documents; to negotiate settlements.

N. RECORDS AND DOCUMENTS
To access my financial records, tax returns, and other documents; to request information from third parties.

O. DELEGATION
To delegate any of these powers to another person or institution, except as limited by law.

P. DIGITAL ASSETS
To access, manage, and control my digital assets, including online accounts, email, social media, cryptocurrency, and digital files, subject to applicable laws and service provider terms.`,
    usageCount: 68,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-poa-healthcare-specific',
    name: 'Healthcare Power of Attorney - Detailed Provisions',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Comprehensive healthcare agent powers and instructions',
    content: `HEALTHCARE POWER OF ATTORNEY - SPECIFIC POWERS AND INSTRUCTIONS

POWERS OF MY HEALTHCARE AGENT

I grant my Healthcare Agent full power and authority to make all healthcare decisions for me if I am unable to make or communicate such decisions myself, including but not limited to:

A. GENERAL HEALTHCARE DECISIONS
- Consent to, refuse, or withdraw consent for any medical treatment, procedure, or service
- Select and discharge healthcare providers and facilities
- Access my medical records and authorize their disclosure
- Apply for Medicare, Medicaid, and other programs on my behalf

B. END-OF-LIFE DECISIONS
{{#if authorizeEndOfLife}}
- Make decisions regarding life-sustaining treatment, including artificial nutrition and hydration, mechanical ventilation, cardiopulmonary resuscitation, and dialysis
- Authorize hospice care and comfort measures
- Decide whether to continue or discontinue treatment based on my expressed wishes and best interests
{{/if}}

C. MENTAL HEALTH TREATMENT
{{#if authorizeMentalHealth}}
- Consent to voluntary admission to a mental health facility
- Consent to mental health treatment, including medication
- Access mental health records
{{/if}}

D. ANATOMICAL GIFTS
{{#if authorizeOrganDonation}}
- Make decisions regarding organ, tissue, and body donation
- Consent to autopsy
{{/if}}

E. DISPOSITION OF REMAINS
{{#if authorizeDisposition}}
- Make decisions regarding the disposition of my remains
- Plan funeral and memorial services
{{/if}}

MY SPECIFIC INSTRUCTIONS AND PREFERENCES

F. VALUES AND BELIEFS
{{#if valuesStatement}}
{{valuesStatement}}
{{#else}}
[Optional statement of values, religious beliefs, or other factors that should guide healthcare decisions]
{{/if}}

G. QUALITY OF LIFE CONSIDERATIONS
In making decisions about my care, my Healthcare Agent should consider:
- My desire to be free from pain and suffering
- My preferences regarding independence and dignity
- The likely outcome and burden of proposed treatments
- My previously expressed wishes and values

H. PERSONS TO CONSULT
My Healthcare Agent should consult with the following persons when making significant healthcare decisions:
{{#each consultPersons}}
- {{this.name}} ({{this.relationship}}): {{this.phone}}
{{/each}}

I. SPECIFIC TREATMENT PREFERENCES
{{#if treatmentPreferences}}
{{treatmentPreferences}}
{{/if}}

J. HIPAA AUTHORIZATION
I authorize my Healthcare Agent and any alternate to receive my protected health information and to discuss my condition with healthcare providers. This authorization survives my incapacity.`,
    usageCount: 55,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-living-will-detailed',
    name: 'Living Will - Detailed End-of-Life Instructions',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Comprehensive living will with specific treatment directives',
    content: `DECLARATION (LIVING WILL)

I, {{principal.name}}, make this declaration to express my wishes regarding medical treatment if I am unable to make or communicate my own decisions.

CIRCUMSTANCES WHEN THIS DECLARATION APPLIES

This declaration applies if I have:
☐ A terminal condition with no reasonable medical probability of recovery
☐ An end-stage condition caused by injury, disease, or illness
☐ A persistent vegetative state with no consciousness or cognitive function
☐ An advanced progressive illness with no reasonable hope of improvement

INSTRUCTIONS REGARDING LIFE-SUSTAINING TREATMENT

If I am in any of the above conditions, I direct that:

A. ARTIFICIAL NUTRITION AND HYDRATION (Tube Feeding)
{{#if wishArtificialNutrition}}
☒ I WANT artificial nutrition and hydration even if it only prolongs the dying process.
☐ I DO NOT WANT artificial nutrition and hydration if it only prolongs the dying process.
{{#else}}
☐ I WANT artificial nutrition and hydration even if it only prolongs the dying process.
☒ I DO NOT WANT artificial nutrition and hydration if it only prolongs the dying process.
{{/if}}

B. MECHANICAL VENTILATION (Breathing Machine)
{{#if wishVentilator}}
☒ I WANT mechanical ventilation.
☐ I DO NOT WANT mechanical ventilation if it only prolongs the dying process.
{{#else}}
☐ I WANT mechanical ventilation.
☒ I DO NOT WANT mechanical ventilation if it only prolongs the dying process.
{{/if}}

C. CARDIOPULMONARY RESUSCITATION (CPR)
{{#if wishCPR}}
☒ I WANT CPR attempted if my heart stops.
☐ I DO NOT WANT CPR if my heart stops.
{{#else}}
☐ I WANT CPR attempted if my heart stops.
☒ I DO NOT WANT CPR if my heart stops.
{{/if}}

D. DIALYSIS
{{#if wishDialysis}}
☒ I WANT dialysis.
☐ I DO NOT WANT dialysis if it only prolongs the dying process.
{{#else}}
☐ I WANT dialysis.
☒ I DO NOT WANT dialysis if it only prolongs the dying process.
{{/if}}

E. ANTIBIOTICS AND OTHER MEDICATIONS
{{#if wishAntibiotics}}
☒ I WANT antibiotics and medications to treat infections and other conditions.
☐ I DO NOT WANT aggressive medical treatment if it only prolongs the dying process.
{{#else}}
☐ I WANT antibiotics and medications to treat infections and other conditions.
☒ I DO NOT WANT aggressive medical treatment if it only prolongs the dying process.
{{/if}}

COMFORT CARE

I ALWAYS want comfort care, including:
- Adequate pain medication, even if it may hasten my death
- Medication for anxiety, agitation, or restlessness
- Hygiene care
- Mouth care
- Relief from nausea
- Being kept warm

HOSPICE CARE

{{#if wishHospice}}
I desire hospice care when appropriate, focusing on comfort rather than cure.
{{/if}}

ADDITIONAL INSTRUCTIONS

{{#if additionalInstructions}}
{{additionalInstructions}}
{{/if}}

This declaration represents my wishes. I understand its full meaning and make it voluntarily.`,
    usageCount: 62,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // BENEFICIARY DESIGNATION AND PLANNING CLAUSES
  {
    id: 'ep-beneficiary-designation-ira',
    name: 'IRA Beneficiary Designation',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Template for IRA beneficiary designation form',
    content: `BENEFICIARY DESIGNATION - INDIVIDUAL RETIREMENT ACCOUNT

Account Owner: {{owner.name}}
Account Number: {{accountNumber}}
Custodian: {{custodian}}

PRIMARY BENEFICIARY(IES)

I designate the following as primary beneficiary(ies) of this account:

{{#each primaryBeneficiaries}}
Name: {{this.name}}
Relationship: {{this.relationship}}
Date of Birth: {{this.dateOfBirth}}
SSN: {{this.ssn}}
Address: {{this.address}}
Share: {{this.percentage}}%
{{#if this.perStirpes}}(Per Stirpes){{/if}}
{{/each}}

Total: 100%

CONTINGENT BENEFICIARY(IES)

If all primary beneficiaries predecease me or disclaim their interest, I designate the following as contingent beneficiary(ies):

{{#each contingentBeneficiaries}}
Name: {{this.name}}
Relationship: {{this.relationship}}
Date of Birth: {{this.dateOfBirth}}
SSN: {{this.ssn}}
Address: {{this.address}}
Share: {{this.percentage}}%
{{#if this.perStirpes}}(Per Stirpes){{/if}}
{{/each}}

Total: 100%

SPECIAL INSTRUCTIONS

{{#if specialInstructions}}
{{specialInstructions}}
{{/if}}

{{#if trustBeneficiary}}
TRUST AS BENEFICIARY
I designate the following trust as beneficiary: {{trustName}}, dated {{trustDate}}.
The trust is intended to qualify as a "see-through" trust under IRS regulations.
{{/if}}

PER STIRPES DESIGNATION

☐ If any beneficiary predeceases me, that beneficiary's share shall pass to their descendants, per stirpes.

SIMULTANEOUS DEATH

If any beneficiary dies simultaneously with me or within {{survivalPeriod | default:"30"}} days after my death, that beneficiary shall be deemed to have predeceased me.

This beneficiary designation supersedes all prior designations for this account.

Signature: _______________________
Date: {{executionDate}}`,
    usageCount: 48,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-disclaimer-provision',
    name: 'Disclaimer/Renunciation Provision',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Provision allowing beneficiaries to disclaim inheritances',
    content: `DISCLAIMER PROVISION

A. RIGHT TO DISCLAIM

Any beneficiary under this [Will/Trust] may disclaim or renounce all or any part of their interest by delivering a written disclaimer that meets the requirements of:
- Internal Revenue Code Section 2518 (for federal tax purposes), and
- The disclaimer statute of the state having jurisdiction.

B. EFFECT OF DISCLAIMER

Any disclaimed interest shall pass as if the disclaiming beneficiary had predeceased me (or the event triggering the distribution) without descendants, unless otherwise specified in the disclaimer.

C. PARTIAL DISCLAIMER

A beneficiary may make a partial disclaimer of:
- A fractional share or percentage of their interest
- Specific assets
- Income as opposed to principal (or vice versa)
- A present interest as opposed to a future interest

D. TIMING

To be effective for federal tax purposes, a disclaimer must be:
- In writing
- Delivered within nine (9) months of the date of transfer (or, for a person under age 21, within nine months of reaching age 21)
- Made before the beneficiary accepts any benefit from the disclaimed interest

E. ADDITIONAL DISCLAIMERS

This provision does not limit other rights of disclaimer that may exist under applicable law.

F. NOTICE

A beneficiary making a disclaimer should provide written notice to the Personal Representative, Trustee, or other fiduciary.

G. NO DIRECTION OF DISCLAIMED PROPERTY

A disclaiming beneficiary may not direct who shall receive the disclaimed property; it shall pass according to the terms of this instrument as if the disclaiming beneficiary had predeceased.`,
    usageCount: 35,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-simultaneous-death',
    name: 'Simultaneous Death / Survivorship Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Provisions addressing simultaneous death of testator and beneficiary',
    content: `SURVIVORSHIP REQUIREMENT AND SIMULTANEOUS DEATH

A. SURVIVORSHIP PERIOD

For purposes of this [Will/Trust], a beneficiary must survive me by at least {{survivalDays | default:"30"}} days to be entitled to receive any distribution. If a beneficiary does not survive me by this period, the beneficiary shall be deemed to have predeceased me.

B. SIMULTANEOUS DEATH

If any beneficiary and I die under circumstances that make it difficult or impossible to determine who died first:
{{#if presumePredeceased}}
That beneficiary shall be presumed to have predeceased me, regardless of any statutory presumption to the contrary.
{{#else}}
The provisions of the Uniform Simultaneous Death Act or similar state law shall apply.
{{/if}}

C. APPLICATION TO SPOUSE

{{#if applyToSpouse}}
This survivorship requirement applies to my spouse. If my spouse does not survive me by at least {{survivalDays | default:"30"}} days, my spouse shall be deemed to have predeceased me for all purposes of this instrument.
{{#else}}
This survivorship requirement does not apply to bequests to my spouse that are intended to qualify for the marital deduction.
{{/if}}

D. COMMON DISASTER

If my spouse and I die as a result of a common disaster or under circumstances that make it difficult to determine the order of our deaths:
- Property passing under this instrument shall pass as if I survived my spouse
- Property passing under my spouse's estate plan shall pass as if my spouse survived me

E. EXCEPTIONS

This survivorship requirement shall not apply to:
- Property that must pass outright to qualify for the marital deduction (unless specifically stated)
- Specific bequests where I have indicated a different intent
- Insurance proceeds or retirement benefits governed by separate beneficiary designations

F. PURPOSE

The purpose of this provision is to:
- Avoid multiple probate proceedings
- Ensure orderly distribution of property
- Achieve tax efficiency
- Prevent property from passing to the family of a beneficiary who only briefly survived me`,
    usageCount: 52,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // FIDUCIARY PROVISIONS
  {
    id: 'ep-trustee-succession',
    name: 'Trustee Succession and Resignation',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Provisions for trustee succession, removal, and resignation',
    content: `TRUSTEE SUCCESSION AND RELATED PROVISIONS

A. INITIAL TRUSTEE
{{grantor.name}} shall serve as initial Trustee.

B. SUCCESSOR TRUSTEES
If the initial Trustee ceases to serve for any reason, the following shall serve as successor Trustee in the order listed:
1. {{firstSuccessor.name}}
2. {{secondSuccessor.name}}
3. {{thirdSuccessor.name | default:"A corporate trustee selected by the beneficiaries"}}

C. RESIGNATION
Any Trustee may resign by giving at least thirty (30) days' written notice to:
- The Grantor (if living and competent)
- The then-current beneficiaries
- Any successor Trustee

D. REMOVAL
{{#if grantorCanRemove}}
During the Grantor's lifetime, the Grantor may remove any Trustee with or without cause.
{{/if}}

{{#if beneficiariesCanRemove}}
After the Grantor's death (or incapacity), a majority of the current adult beneficiaries may remove any Trustee with or without cause by written notice.
{{/if}}

E. FILLING VACANCIES
If no successor Trustee is willing or able to serve:
{{#if courtAppointment}}
A court of competent jurisdiction may appoint a successor Trustee upon petition by any interested party.
{{#else}}
The remaining beneficiaries (or their guardians if minor) may select a successor Trustee by majority vote.
{{/if}}

F. CO-TRUSTEES
{{#if allowCoTrustees}}
Two or more Trustees may serve together as Co-Trustees. Unless otherwise specified:
- Co-Trustees shall act by majority decision
- Investment responsibilities may be delegated to one Co-Trustee
- Each Co-Trustee shall be responsible only for their own acts or omissions
{{/if}}

G. CORPORATE TRUSTEE
Any corporate Trustee shall be a bank or trust company authorized to act as trustee in the state where the trust is administered.

H. ACCEPTANCE
A Trustee accepts appointment by:
- Written acceptance
- Beginning to act as Trustee
- Failing to disclaim within a reasonable time after notice

I. BOND
No Trustee shall be required to furnish bond or other security, unless required by law or court order.

J. TRANSITION
Upon resignation or removal, the outgoing Trustee shall:
- Deliver all trust property to the successor Trustee
- Provide a final accounting
- Execute documents necessary to transfer title to trust assets`,
    usageCount: 45,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-fiduciary-compensation',
    name: 'Fiduciary Compensation Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Provisions for trustee and personal representative compensation',
    content: `FIDUCIARY COMPENSATION

A. INDIVIDUAL TRUSTEE COMPENSATION
{{#if individualTrusteeCompensation}}
An individual serving as Trustee shall be entitled to reasonable compensation for services rendered, not to exceed the compensation that would be charged by a corporate trustee in the same locality for similar services.
{{#else}}
An individual serving as Trustee shall serve without compensation, but shall be entitled to reimbursement for reasonable expenses incurred in the administration of the trust.
{{/if}}

B. CORPORATE TRUSTEE COMPENSATION
A corporate Trustee shall be entitled to compensation in accordance with its published fee schedule in effect at the time services are rendered, including:
- Annual administration fees
- Transaction fees
- Termination fees

C. PERSONAL REPRESENTATIVE COMPENSATION
{{#if prCompensation}}
My Personal Representative shall be entitled to reasonable compensation as provided by law, or as agreed upon with the beneficiaries.
{{#else}}
My Personal Representative shall serve without compensation, but shall be entitled to reimbursement for reasonable expenses.
{{/if}}

D. PROFESSIONAL SERVICES
The fiduciary may employ and pay from the trust or estate:
- Attorneys
- Accountants
- Investment advisors
- Custodians
- Appraisers
- Tax preparers
- Other professionals

Such employment shall not be considered a delegation of fiduciary responsibility, and the fiduciary shall not be liable for the acts of such professionals if selected with reasonable care.

E. GUARDIAN COMPENSATION
{{#if guardianCompensation}}
A guardian of the person serving under this instrument shall be entitled to reasonable compensation.
{{/if}}

F. EXTRAORDINARY SERVICES
A fiduciary shall be entitled to additional reasonable compensation for extraordinary services, including:
- Sale of real estate
- Litigation
- Tax audits
- Business operations

G. WAIVER
Any fiduciary may waive all or part of their compensation.

H. APPROVAL
{{#if requireApproval}}
Fiduciary compensation shall be subject to approval by {{approvalParty | default:"the beneficiaries"}}.
{{/if}}`,
    usageCount: 38,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-exoneration-clause',
    name: 'Fiduciary Exoneration and Indemnification',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Limitations on fiduciary liability and indemnification provisions',
    content: `FIDUCIARY EXONERATION AND INDEMNIFICATION

A. STANDARD OF CARE
The Trustee shall administer the trust with the care, skill, and caution that a prudent person acting in a like capacity would exercise. The Trustee shall not be liable for any loss or depreciation in value of trust property unless it results from:
- Willful misconduct
- Gross negligence
- Bad faith
- Breach of fiduciary duty

B. EXONERATION
The Trustee shall not be liable for:
1. Any act or omission of a predecessor Trustee
2. Any loss resulting from investments made in good faith
3. Any decrease in value of trust assets not caused by breach of duty
4. Relying in good faith on professional advice
5. Reasonable delays in distributing trust property
6. Actions taken in accordance with written instructions from authorized parties

C. INDEMNIFICATION
The Trustee shall be indemnified from trust assets for:
- Reasonable attorney's fees and costs of litigation
- Any liability incurred while acting in good faith as Trustee
- Expenses incurred in defending against claims related to the trust

D. THIRD-PARTY RELIANCE
No person dealing with the Trustee shall be required to:
- Inquire into the Trustee's authority
- See to the application of funds or property
- Verify the Trustee's compliance with this instrument

Third parties may rely on the Trustee's certification of authority.

E. INVESTMENT STANDARD
{{#if prudentInvestor}}
The Trustee may invest in any property the Trustee considers appropriate, without being limited to investments authorized for fiduciaries under state law, applying the prudent investor standard.
{{#else}}
The Trustee shall invest trust assets as a prudent investor would, considering the purposes, terms, and circumstances of the trust.
{{/if}}

F. RETENTION OF ASSETS
The Trustee may retain any assets received into the trust without liability for loss or depreciation, even if the assets are not diversified or would not otherwise be a prudent investment.

G. EFFECT OF EXONERATION
These exoneration provisions shall not be construed to relieve any Trustee of liability for:
- Self-dealing
- Conflicts of interest not properly disclosed
- Intentional wrongdoing
- Acts in bad faith`,
    usageCount: 42,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // TAX PLANNING CLAUSES
  {
    id: 'ep-tax-allocation-clause',
    name: 'Estate Tax Allocation Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Provisions for allocation of estate taxes among beneficiaries',
    content: `ESTATE TAX ALLOCATION

A. GENERAL RULE
Unless otherwise specified below, all estate, inheritance, and similar death taxes (including interest and penalties) imposed by reason of my death shall be paid from my residuary estate.

B. TAXES ON NON-PROBATE PROPERTY
{{#if apportionNonProbate}}
Taxes attributable to property passing outside of my probate estate (such as jointly held property, retirement accounts, life insurance, and trust property) shall be charged against and paid from such property, in accordance with applicable state apportionment statutes.
{{#else}}
Taxes attributable to property passing outside of my probate estate shall NOT be apportioned against such property but shall be paid from my residuary estate, even if this increases the tax burden on my estate.
{{/if}}

C. TAXES ON MARITAL PROPERTY
Taxes attributable to property qualifying for the marital deduction shall be paid from {{maritalTaxSource | default:"such marital property"}}.

D. TAXES ON QTIP PROPERTY
Estate taxes imposed on property included in my spouse's estate by reason of a QTIP election made with respect to my estate shall be paid from {{qtipTaxSource | default:"the remaining trust property"}}.

E. TAXES ON GENERATION-SKIPPING TRANSFERS
Generation-skipping transfer taxes shall be paid from {{gstTaxSource | default:"the transferred property"}}.

F. NO CONTRIBUTION
{{#if noContribution}}
No beneficiary shall have any right of contribution or reimbursement against any other beneficiary with respect to taxes paid.
{{/if}}

G. TAX ELECTIONS
The Personal Representative and Trustee may make any tax elections they deem advisable, including but not limited to:
- QTIP election
- Allocation of GST exemption
- Alternate valuation date
- Deduction of administration expenses on the estate tax return or income tax return
- Basis allocation elections

These elections shall be binding on all beneficiaries.

H. TAX MINIMIZATION
The fiduciary shall consider the overall tax impact on all beneficiaries when making tax decisions, but shall not be liable for failure to minimize taxes.`,
    usageCount: 55,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-portability-election',
    name: 'Portability Election Provision',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Instruction regarding DSUE (deceased spousal unused exclusion) election',
    content: `PORTABILITY ELECTION (DSUE)

A. INSTRUCTION TO PERSONAL REPRESENTATIVE

I direct my Personal Representative to determine whether making the portability election under Internal Revenue Code Section 2010(c)(5) would benefit my surviving spouse and my overall estate plan.

{{#if mandatoryPortability}}
My Personal Representative SHALL file an estate tax return and make the portability election to transfer any unused portion of my applicable exclusion amount to my surviving spouse, even if an estate tax return would not otherwise be required.
{{#else}}
My Personal Representative MAY, in their discretion, file an estate tax return to make the portability election if the Personal Representative determines it would be beneficial to my surviving spouse.
{{/if}}

B. COSTS OF ELECTION

The costs of filing an estate tax return solely to make the portability election shall be paid from {{portabilityCostSource | default:"my residuary estate"}}.

C. FACTORS TO CONSIDER

In deciding whether to make the portability election, my Personal Representative should consider:
- My surviving spouse's expected estate
- Potential changes in estate tax exemption amounts
- Cost of filing the return
- Availability of my surviving spouse's own exemption
- Any applicable state estate tax implications
- The potential for remarriage

D. NO LIABILITY

My Personal Representative shall not be liable for making or failing to make the portability election if the decision is made in good faith after reasonable consideration.

E. COOPERATION

My surviving spouse and all beneficiaries shall cooperate with my Personal Representative in making the portability election, including providing necessary information and signing required documents.

F. DEADLINE

The portability election must be made on a timely filed estate tax return, including extensions. If no return is required, a simplified return may be filed within the normal deadline (typically 9 months after death, with extensions).`,
    usageCount: 32,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  // MISCELLANEOUS ESTATE PLANNING PROVISIONS
  {
    id: 'ep-no-contest-in-terrorem',
    name: 'No-Contest (In Terrorem) Clause',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Provision to discourage beneficiaries from contesting will or trust',
    content: `NO-CONTEST CLAUSE (IN TERROREM)

A. FORFEITURE PROVISION

If any beneficiary under this [Will/Trust], directly or indirectly:

1. Contests or attacks this [Will/Trust] or any provision hereof;
2. Contests the validity of this [Will/Trust] or any amendment;
3. Seeks to obtain an adjudication that this [Will/Trust] or any provision is void;
4. Challenges any fiduciary named herein;
5. Files any creditor's claim against my estate;
6. Seeks to have any property transferred or distributed other than as provided herein;
7. Aids, encourages, or assists any person in taking any such action;

then such beneficiary shall forfeit their entire interest under this [Will/Trust] and shall be treated as having predeceased me.

B. EXCEPTIONS

This no-contest clause shall NOT be triggered by:

1. A beneficiary's good faith contest of the interpretation of any ambiguous provision;
2. Actions to enforce fiduciary duties or obtain an accounting;
3. Proceedings to determine heirship or beneficiary status;
4. Actions brought in good faith with probable cause, as determined by a court;
5. Assertion of rights under applicable law that cannot be waived;
6. Disclaimer of an inheritance.

C. DISINHERITED PERSONS

Any person who is not otherwise a beneficiary under this [Will/Trust] who contests it shall receive nothing.

D. ENFORCEABILITY

I understand that the enforceability of this clause varies by jurisdiction. It is my intent that this clause be enforced to the maximum extent permitted by law.

E. NOMINAL GIFT

{{#if nominalGift}}
Each beneficiary who would otherwise be disinherited by this clause shall instead receive \${{nominalAmount | default:"100"}} as their entire interest, to give them standing to invoke the protections of this clause.
{{/if}}

F. PURPOSE

The purpose of this clause is to ensure that my estate plan is carried out according to my wishes without the expense, delay, and family discord that can result from litigation.`,
    usageCount: 48,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-digital-assets',
    name: 'Digital Assets Provision',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Authority to access and manage digital assets and accounts',
    content: `DIGITAL ASSETS PROVISION

A. DEFINITION OF DIGITAL ASSETS

For purposes of this [Will/Trust/POA], "digital assets" includes:
- Email accounts and their contents
- Social media accounts (Facebook, Instagram, Twitter/X, LinkedIn, etc.)
- Cloud storage accounts and stored files
- Cryptocurrency and blockchain assets
- Domain names and websites
- Digital photos, videos, and documents
- Online financial accounts
- Gaming accounts and virtual property
- Loyalty program accounts
- Digital music, books, and media libraries
- Business accounts and records
- Any other electronic records or accounts

B. AUTHORITY GRANTED

I authorize and direct my {{#if trust}}Trustee{{#elseif poa}}Agent{{#else}}Personal Representative{{/if}} to:

1. Access, manage, and control my digital assets
2. Obtain access credentials and passwords from any source
3. Request and obtain the contents of my digital accounts
4. Request that online accounts be closed, deleted, or memorialized
5. Transfer digital assets to beneficiaries or fiduciaries
6. Decrypt encrypted data
7. Take any other action regarding digital assets as I could have taken during my lifetime

C. CONSENT TO DISCLOSURE

I hereby consent to the disclosure of my digital assets and the contents of my electronic communications to my fiduciary. This consent is intended to satisfy the requirements of:
- The Revised Uniform Fiduciary Access to Digital Assets Act (RUFADAA)
- The Stored Communications Act
- Any similar state or federal law

D. SERVICE PROVIDER TERMS

To the extent permitted by law, I authorize my fiduciary to access my digital assets even if doing so would otherwise violate service provider terms of service.

E. CRYPTOCURRENCY INSTRUCTIONS

{{#if hasCrypto}}
I hold cryptocurrency or other blockchain-based assets. Information regarding the location of these assets and access credentials is:
{{cryptoInstructions | default:"[Specify location of wallet information, private keys, or seed phrases]"}}
{{/if}}

F. PASSWORD DOCUMENT

{{#if hasPasswordDoc}}
I maintain a document listing my digital accounts and passwords. This document is located:
{{passwordLocation}}
{{/if}}

G. SPECIFIC INSTRUCTIONS

{{#if digitalInstructions}}
{{digitalInstructions}}
{{/if}}`,
    usageCount: 42,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-burial-funeral-instructions',
    name: 'Burial and Funeral Instructions',
    category: 'Estate Planning',
    jurisdictions: ['WI', 'FL'],
    description: 'Detailed instructions for funeral, burial, or cremation',
    content: `BURIAL AND FUNERAL INSTRUCTIONS

I express the following wishes regarding the disposition of my remains and funeral arrangements. While I understand these instructions may not be legally binding in all jurisdictions, I ask that my family and fiduciaries honor my wishes.

A. DISPOSITION OF REMAINS

{{#if cremation}}
☒ CREMATION
I direct that my remains be cremated. My ashes shall be:
{{cremationDisposition | default:"[Specify: scattered, interred, kept by family, etc.]"}}
{{#else}}
☒ BURIAL
I direct that my remains be buried at:
{{burialLocation | default:"[Specify cemetery or location]"}}

{{#if specificPlot}}
Plot/Location: {{plotDetails}}
{{/if}}
{{/if}}

B. FUNERAL SERVICE

{{#if servicePreferences}}
☒ I want a funeral/memorial service with the following preferences:
- Type of service: {{serviceType | default:"[Religious/secular/celebration of life]"}}
- Location: {{serviceLocation | default:"[Specify]"}}
- Officiant: {{officiant | default:"[Specify or leave to family]"}}
- Music: {{musicPreferences}}
- Readings: {{readingPreferences}}
- Speakers: {{speakerPreferences}}
{{#else}}
☐ I do not want a funeral or memorial service.
{{/if}}

C. VISITATION/VIEWING

{{#if wantViewing}}
☒ I want a visitation/viewing.
   ☐ Open casket
   ☐ Closed casket
{{#else}}
☐ I do not want a visitation or viewing.
{{/if}}

D. ORGAN DONATION

{{#if organDonation}}
☒ I am registered as an organ donor / I wish to donate my organs and tissues.
   {{#if organRestrictions}}Restrictions: {{organRestrictions}}{{/if}}
{{#else}}
☐ I do not wish to donate my organs or tissues.
{{/if}}

E. BODY DONATION

{{#if bodyDonation}}
☒ I wish to donate my body to medical science.
   Institution: {{donationInstitution}}
{{/if}}

F. MILITARY HONORS

{{#if veteran}}
☒ I am a veteran and request military honors.
   Branch: {{militaryBranch}}
   Rank: {{militaryRank}}
{{/if}}

G. OBITUARY

{{#if obituaryPreferences}}
My preferences for my obituary:
{{obituaryPreferences}}
{{/if}}

H. PREPAID ARRANGEMENTS

{{#if prepaidArrangements}}
I have prepaid funeral arrangements with:
{{funeralHome}}
Contract Number: {{contractNumber}}
{{/if}}

I. FUNDING

Funeral expenses shall be paid from {{funeralFunding | default:"my estate"}}.

J. PERSON TO MAKE ARRANGEMENTS

I designate {{arrangementsPerson.name}} to make final arrangements. If unavailable, {{alternateArrangementsPerson.name}} shall make arrangements.`,
    usageCount: 55,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-florida-homestead',
    name: 'Florida Homestead Provisions',
    category: 'Estate Planning',
    jurisdictions: ['FL'],
    description: 'Special provisions addressing Florida constitutional homestead restrictions',
    content: `FLORIDA HOMESTEAD PROVISIONS

A. ACKNOWLEDGMENT OF FLORIDA HOMESTEAD RESTRICTIONS

I acknowledge that Article X, Section 4 of the Florida Constitution restricts my ability to devise my homestead property if I am survived by a spouse or minor child.

B. DEFINITION

My "homestead" for purposes of this [Will/Trust] means the real property that qualifies as my homestead under the Florida Constitution at the time of my death.

C. DEVISE OF HOMESTEAD

{{#if survivedBySpouse}}
IF SURVIVED BY SPOUSE:

Option 1 - Life Estate:
☐ I devise my homestead to my spouse, {{spouse.name}}, for life, with the remainder to {{remainderBeneficiaries}}.

Option 2 - Fee Simple to Spouse (with spouse's written consent):
☐ I devise my homestead to my spouse, {{spouse.name}}, in fee simple. My spouse has joined in this [Will/Trust] to consent to this devise.

Option 3 - Tenancy by the Entireties (if applicable):
☐ My homestead is owned as tenancy by the entireties with my spouse and will pass to my spouse by operation of law.
{{/if}}

{{#if noSurvivingSpouseOrMinor}}
IF NOT SURVIVED BY SPOUSE OR MINOR CHILD:

If I am not survived by a spouse or minor child, I devise my homestead to {{homesteadBeneficiary}}.
{{/if}}

D. HOMESTEAD EXEMPT FROM CLAIMS

I understand that my Florida homestead is exempt from forced sale by creditors, and I intend for this protection to continue to the extent permitted by law.

E. SPOUSE'S ELECTION

{{#if spouseElection}}
My spouse shall have the right to elect to take:
- A life estate in the homestead (with remainder to my descendants), OR
- An undivided one-half interest in the homestead as a tenant in common

This election must be made within six (6) months of my death, as required by Florida law.
{{/if}}

F. INTENT

It is my intent that my homestead pass in a manner consistent with the Florida Constitution. If any provision herein conflicts with constitutional requirements, the constitutional requirements shall prevail.

G. WAIVER

{{#if spouseWaiver}}
My spouse, {{spouse.name}}, has executed a valid waiver of homestead rights in accordance with Florida Statutes Section 732.702.
{{/if}}

H. NON-HOMESTEAD PROPERTY

Property that does not qualify as my homestead under the Florida Constitution shall pass according to the other provisions of this [Will/Trust].`,
    usageCount: 35,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-wisconsin-marital-property',
    name: 'Wisconsin Marital Property Provisions',
    category: 'Estate Planning',
    jurisdictions: ['WI'],
    description: 'Provisions addressing Wisconsin community property (marital property) law',
    content: `WISCONSIN MARITAL PROPERTY ACT PROVISIONS

A. ACKNOWLEDGMENT

I acknowledge that Wisconsin is a community property (marital property) state under Chapter 766 of the Wisconsin Statutes.

B. CLASSIFICATION OF PROPERTY

For purposes of this [Will/Trust]:

1. MARITAL PROPERTY: Property acquired by either spouse during marriage while domiciled in Wisconsin, except for property classified as individual property.

2. INDIVIDUAL PROPERTY: Property owned before marriage, acquired by gift or inheritance, or designated as individual property by written agreement.

3. MIXED PROPERTY: Property that has both marital and individual components.

C. DISPOSITION OF MARITAL PROPERTY

{{#if disposeAllMarital}}
I dispose of all marital property, including my one-half interest and any interest I have in my spouse's one-half interest by reason of survivorship rights.
{{#else}}
I dispose of my one-half interest in marital property only. My spouse's one-half interest shall pass according to my spouse's estate plan or by operation of law.
{{/if}}

D. SURVIVORSHIP MARITAL PROPERTY

{{#if survivorshipMarital}}
My spouse, {{spouse.name}}, and I have classified certain property as "survivorship marital property" under Wisconsin Statutes Section 766.60. Such property shall pass to the surviving spouse by operation of law and is not subject to disposition under this [Will/Trust].
{{/if}}

E. MARITAL PROPERTY AGREEMENT

{{#if mpaExists}}
My spouse and I have executed a Marital Property Agreement dated {{mpaDate}}. The terms of that agreement shall control the classification and disposition of property covered by it.
{{/if}}

F. SPOUSE'S CONSENT

{{#if spouseConsent}}
My spouse, {{spouse.name}}, has consented to the disposition of marital property under this [Will/Trust] by signing below or in a separate written consent.

_____________________________
{{spouse.name}}, Spouse
{{/if}}

G. DEFERRED MARITAL PROPERTY

Upon my death, any deferred marital property (appreciation of individual property during marriage) shall be treated as {{deferredTreatment | default:"marital property"}}.

H. AUGMENTED MARITAL PROPERTY ESTATE

I am aware that my spouse may have rights under Wisconsin's augmented marital property estate provisions. These provisions may require that my spouse receive a minimum share of our combined property.

I. INDIVIDUAL PROPERTY

I intend that the following property, which I classify as my individual property, pass under this [Will/Trust]:
{{#each individualProperty}}
- {{this.description}}: {{this.basis}}
{{/each}}`,
    usageCount: 28,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-self-proving-affidavit-fl',
    name: 'Self-Proving Affidavit (Florida)',
    category: 'Estate Planning',
    jurisdictions: ['FL'],
    description: 'Florida self-proving affidavit to be attached to will',
    content: `SELF-PROVING AFFIDAVIT
(Florida Statutes Section 732.503)

STATE OF FLORIDA
COUNTY OF {{county | uppercase}}

Before me, the undersigned authority, personally appeared {{testator.name}}, {{witness1.name}}, and {{witness2.name}}, known to me to be the testator and the witnesses, respectively, whose names are signed to the foregoing instrument, and all being duly sworn, {{testator.name}}, the testator, declared to me and to the witnesses in my presence that said instrument is the testator's last will and that the testator had willingly signed and executed it as the testator's free and voluntary act for the purposes therein expressed; and each of the witnesses stated that they signed the will as witness in the presence and at the request of the testator and in the presence of each other.

_____________________________
{{testator.name}}, Testator

The foregoing instrument was acknowledged before me this {{executionDate | date:"d"}} day of {{executionDate | date:"MMMM"}}, {{executionDate | date:"yyyy"}}.

_____________________________
{{witness1.name}}, Witness
Address: ______________________

_____________________________
{{witness2.name}}, Witness
Address: ______________________

Subscribed, sworn to and acknowledged before me by {{testator.name}}, the testator, and subscribed and sworn to before me by {{witness1.name}} and {{witness2.name}}, witnesses, this {{executionDate | date:"d"}} day of {{executionDate | date:"MMMM"}}, {{executionDate | date:"yyyy"}}.

_____________________________
Notary Public, State of Florida
Print Name: ___________________
My Commission Expires: _________
Commission Number: ____________

[NOTARY SEAL]`,
    usageCount: 65,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    id: 'ep-self-proving-affidavit-wi',
    name: 'Self-Proving Affidavit (Wisconsin)',
    category: 'Estate Planning',
    jurisdictions: ['WI'],
    description: 'Wisconsin self-proving affidavit to be attached to will',
    content: `SELF-PROVING AFFIDAVIT
(Wisconsin Statutes Section 853.04)

STATE OF WISCONSIN
COUNTY OF {{county | uppercase}}

We, {{testator.name}}, {{witness1.name}}, and {{witness2.name}}, the testator and the witnesses, respectively, whose names are signed to the foregoing instrument, being first duly sworn, do hereby declare to the undersigned authority that the testator signed and executed the instrument as the testator's will and that each of the witnesses, in the presence and at the request of the testator, signed the will as witness.

_____________________________
{{testator.name}}, Testator

We, the witnesses, sign our names to this instrument, being first duly sworn, and do hereby declare to the undersigned authority that the testator signed and executed this instrument as the testator's will and signed willingly (or willingly directed another to sign), and that each of us, in the presence of the testator, signed the will as witness.

_____________________________
{{witness1.name}}, Witness
Address: ______________________

_____________________________
{{witness2.name}}, Witness
Address: ______________________

STATE OF WISCONSIN
COUNTY OF {{county | uppercase}}

Subscribed, sworn to and acknowledged before me by {{testator.name}}, the testator, and subscribed and sworn to before me by {{witness1.name}} and {{witness2.name}}, witnesses, this {{executionDate | date:"d"}} day of {{executionDate | date:"MMMM"}}, {{executionDate | date:"yyyy"}}.

_____________________________
Notary Public, State of Wisconsin
Print Name: ___________________
My Commission: ________________

[NOTARY SEAL]`,
    usageCount: 58,
    lastModified: new Date('2024-12-06'),
    createdAt: new Date('2024-11-15'),
    createdBy: 'admin',
    updatedBy: 'admin',
  },
];

// API Functions
async function fetchClauses(filters?: ClauseFilters): Promise<Clause[]> {
  try {
    const constraints = [];

    if (filters?.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters?.jurisdiction) {
      constraints.push(where('jurisdictions', 'array-contains', filters.jurisdiction));
    }

    constraints.push(orderBy('lastModified', 'desc'));

    const q = query(collection(db, 'clauses'), ...constraints);
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Return mock data if no Firestore data
      let filtered = [...mockClauses];
      if (filters?.category) {
        filtered = filtered.filter(c => c.category === filters.category);
      }
      if (filters?.jurisdiction) {
        filtered = filtered.filter(c => c.jurisdictions.includes(filters.jurisdiction!));
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter(c =>
          c.name.toLowerCase().includes(search) ||
          c.description?.toLowerCase().includes(search)
        );
      }
      return filtered;
    }

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        lastModified: timestampToDate(data.lastModified),
        createdAt: timestampToDate(data.createdAt),
      } as Clause;
    });
  } catch (error) {
    console.error('Error fetching clauses:', error);
    return mockClauses;
  }
}

async function fetchClause(id: string): Promise<Clause | null> {
  try {
    const docSnap = await getDoc(doc(db, 'clauses', id));

    if (!docSnap.exists()) {
      // Check mock data
      return mockClauses.find(c => c.id === id) || null;
    }

    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      lastModified: timestampToDate(data.lastModified),
      createdAt: timestampToDate(data.createdAt),
    } as Clause;
  } catch (error) {
    console.error('Error fetching clause:', error);
    return mockClauses.find(c => c.id === id) || null;
  }
}

async function createClause(data: ClauseFormData, userId: string): Promise<Clause> {
  const id = data.name.toLowerCase().replace(/\s+/g, '_') || generateId();

  const clauseData = {
    ...data,
    id,
    description: data.description || '',
    usageCount: 0,
    lastModified: serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedBy: userId,
  };

  try {
    await setDoc(doc(db, 'clauses', id), clauseData);

    return {
      ...data,
      id,
      description: data.description || '',
      usageCount: 0,
      lastModified: new Date(),
      createdAt: new Date(),
      createdBy: userId,
      updatedBy: userId,
    };
  } catch (error) {
    console.error('Error creating clause:', error);
    // Return local version on error
    return {
      ...data,
      id,
      description: data.description || '',
      usageCount: 0,
      lastModified: new Date(),
      createdAt: new Date(),
      createdBy: userId,
      updatedBy: userId,
    };
  }
}

async function updateClause(
  id: string,
  data: Partial<ClauseFormData>,
  userId: string
): Promise<Clause> {
  try {
    await updateDoc(doc(db, 'clauses', id), {
      ...data,
      lastModified: serverTimestamp(),
      updatedBy: userId,
    });

    const updated = await fetchClause(id);
    return updated!;
  } catch (error) {
    console.error('Error updating clause:', error);
    throw error;
  }
}

async function deleteClause(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'clauses', id));
  } catch (error) {
    console.error('Error deleting clause:', error);
    throw error;
  }
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch list of clauses
 */
export function useClauses(filters?: ClauseFilters) {
  return useQuery({
    queryKey: clauseKeys.list(filters),
    queryFn: () => fetchClauses(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch a single clause by ID
 */
export function useClause(id: string) {
  return useQuery({
    queryKey: clauseKeys.detail(id),
    queryFn: () => fetchClause(id),
    enabled: !!id,
  });
}

/**
 * Fetch clauses by category
 */
export function useClausesByCategory(category: ClauseCategory) {
  return useQuery({
    queryKey: clauseKeys.byCategory(category),
    queryFn: () => fetchClauses({ category }),
    staleTime: 1000 * 60 * 5,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Create a new clause
 */
export function useCreateClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, userId }: { data: ClauseFormData; userId: string }) =>
      createClause(data, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clauseKeys.all });
    },
  });
}

/**
 * Update an existing clause
 */
export function useUpdateClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, userId }: { id: string; data: Partial<ClauseFormData>; userId: string }) =>
      updateClause(id, data, userId),
    onSuccess: (updated) => {
      queryClient.setQueryData(clauseKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: clauseKeys.list() });
    },
  });
}

/**
 * Delete a clause
 */
export function useDeleteClause() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteClause,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clauseKeys.all });
    },
  });
}

export default {
  useClauses,
  useClause,
  useClausesByCategory,
  useCreateClause,
  useUpdateClause,
  useDeleteClause,
  clauseKeys,
};
